if (!customElements.get("cq-cart-drawer")) {
  // The cart page shows Shopify's installments line from this total (cents), the rule in cq-cart-drawer-footer.
  const TERMS_MIN = 5000;
  // In a line's details, what follows the "You save" label (cq-cart-drawer-content), where a held label goes.
  const LINE_AFTER_SAVINGS = ":scope > :is(.cq-cart-plan, .cq-cart-plan-name, .cq-cart-unit-price, .cq-cart-discounts, .cq-cart-line-actions, .cq-cart-error)";
  // One cart system, two surfaces (round 6). The side cart is a modal drawer. The cart page (/cart) renders the same element,
  // the same snippets and the same section targets with data-cq-page: it is always "open" (no dialog, no focus trap, no
  // scroll lock) and its section is the page's main-cart-items instead of header-mini-cart. The layout leaves the drawer off
  // /cart, so the page's ids never meet the drawer's, and every add-to-cart path on the page (recommendations, search, the
  // option picker) lands in the page through the usual mini-cart contract.
  class CQCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.page = this.hasAttribute("data-cq-page");
      this.sectionKey = this.dataset.section || "header-mini-cart";
      this.isOpen = this.page;
      this.pendingRefresh = null;
      this.renderedCarts = new WeakSet();
      this.toastedCarts = new WeakSet();
      this.disabledControls = new Map();
      this.softDisabled = new Map();
      this.variantIds = new Set();
      this.revision = 0;
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      this.addEventListener("click", (event) => this.onClick(event), { capture: true, signal });
      this.addEventListener("change", (event) => {
        if (event.target.matches("[data-cq-quantity-input]")) {
          this.changeQuantity(event.target.closest("[data-key]"), Number(event.target.value));
        } else if (event.target.matches("[data-cq-plan-mode]")) {
          this.onPlanMode(event.target);
        } else if (event.target.matches("[data-cq-line-plan-select]") && event.target.value) {
          this.changePlan(event.target.closest("[data-key]"), event.target.value);
        }
      }, { signal });
      this.addEventListener("keydown", (event) => {
        if (event.key === "Enter" && event.target.id === "CQCartCoupon") {
          event.preventDefault();
          this.applyCoupon();
        } else if ((event.key === "Enter" || event.key === " ") && event.target.id === "shopify-subscription-policy-button") {
          event.preventDefault();
          event.target.click();
        }
      }, { signal });
      this.addEventListener("toggle", (event) => {
        // A discount field reopened by a re-render (restoreCoupon) keeps focus where it is; only the shopper's own open moves it.
        if (event.target.hasAttribute?.("data-cq-quiet")) {
          event.target.removeAttribute("data-cq-quiet");
          return;
        }
        if (this.isOpen && event.target.matches("[data-cq-coupon]") && event.target.open) {
          this.querySelector("#CQCartCoupon")?.focus();
        }
      }, { capture: true, signal });
      document.addEventListener("keydown", (event) => this.onKeydown(event), { capture: true, signal });
      document.addEventListener("focusin", (event) => {
        if (this.isOpen && !this.page && !this.contains(event.target) && !this.modalOpen()) this.focusFirst();
      }, { signal });
      ["cart:item-added", "cart:item-add"].forEach((name) => {
        document.addEventListener(name, (event) => this.onCartEvent(event.detail, true), { signal });
      });
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      document.addEventListener("cart:refresh", (event) => this.onCartEvent(event.detail, false), { signal });
      document.addEventListener("cart:update", (event) => this.onCartEvent(event.detail, false), { signal });
      document.addEventListener("quick-view-drawer:close", () => {
        if (!this.returnFromOptions) return;
        const productId = this.returnFromOptions;
        this.returnFromOptions = null;
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.open(this.openedBy);
          const optionButton = Array.from(this.querySelectorAll("[data-cross-sell-id]")).find((row) => row.dataset.crossSellId === productId)?.querySelector("[data-cq-cross-options]");
          optionButton?.focus();
        });
      }, { signal });
      this.unsubscribers = [];
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            const addedVariant = event?.source === "quick-add" && event?.cartData?.items?.some((item) => !this.variantIds.has(String(item.variant_id || item.id)));
            this.onCartEvent(event, event?.action === "add" || addedVariant);
          }),
          subscribe(PUB_SUB_EVENTS.cartItemAdd, (event) => this.onCartEvent(event, true)),
        );
      }
      const intent = (event) => {
        if (!event.target.closest?.('[aria-controls="HeaderMiniCart"], .head-slot-cart-link')) return;
        this.loadImages();
        this.prefetchRecent();
      };
      ["pointerover", "focusin", "touchstart"].forEach((name) => document.addEventListener(name, intent, { passive: true, signal }));
      if (this.page) {
        this.observeWallets();
        this.initPage();
        this.loadPopular();
      }
      this.observer = new MutationObserver(() => this.syncState());
      ["header-mini-cart-content", "header-mini-cart-footer"].forEach((id) => {
        const target = document.getElementById(id);
        if (target) this.observer.observe(target, { childList: true });
      });
      this.syncState();
      // A page reached with the browser's Back or Forward button can be an old copy: the browser's cache when the page could
      // not be kept alive, or the frozen page when it could. Its cart is the one from when it was first shown, so a shopper
      // back from checkout saw "Cart, 0 items", an empty side cart and no checkout button while the cart still held the
      // order. Read the live cart again in both cases.
      if (performance.getEntriesByType?.("navigation")[0]?.type === "back_forward") this.scheduleRefresh(false);
      window.addEventListener("pageshow", (event) => { if (event.persisted) this.scheduleRefresh(false); }, { signal });
      window.CQLoopDefaults?.load().then((ids) => { if (ids && this.isConnected) this.followLoop(); });
    }

    initDrawerObservers() {
      if (this.page || this.drawerObserversReady) return;
      this.drawerObserversReady = true;
      this.observeWallets();
      this.observeScroll();
    }

    // AJ 2026-10-11 (option A): a one-tap Add on a plan (snippets/cq-cart-drawer-offer.liquid) starts on Loop's default
    // frequency. When the theme's copy of Loop's defaults is out of date, the Add follows Loop's live default in the same
    // plan group (assets/cq-loop-default.js): the plan it sends, its price, the struck one-time price and the saving the
    // cart page previews. Normally the Add already carries Loop's default and nothing changes. Spare cards waiting in
    // the popular row's template are included, so a card that takes a slot later is already right.
    followLoop() {
      const loop = window.CQLoopDefaults;
      if (!loop?.live()) return;
      const roots = [this, ...Array.from(this.querySelectorAll("template"), (template) => template.content)];
      roots.forEach((root) => root.querySelectorAll('.cq-cart-offer input[name="selling_plan"][data-cq-loop-plans]').forEach((input) => {
        let plans;
        try { plans = JSON.parse(input.dataset.cqLoopPlans); } catch (error) { return; }
        const target = loop.follow(input.value, plans);
        if (!target || target === input.value) return;
        const plan = plans.find((entry) => String(entry.id) === target);
        if (!plan) return;
        input.value = target;
        const card = input.closest(".cq-cart-offer");
        const price = card?.querySelector("[data-cq-offer-plan-price]");
        if (price && plan.priceText && price.textContent !== plan.priceText) price.textContent = plan.priceText;
        const label = card?.querySelector("[data-cq-offer-plan-label]");
        if (label && plan.label && label.textContent !== plan.label) label.textContent = plan.label;
        const was = card?.querySelector(".cq-cart-offer-was");
        if (was) was.hidden = !plan.lower;
        if (card && plan.saving > 0) card.dataset.cqSaving = String(plan.saving);
        else if (card) delete card.dataset.cqSaving;
      }));
    }

    disconnectedCallback() {
      this.close(false);
      this.listeners?.abort();
      this.observer?.disconnect();
      this.walletMutations?.disconnect();
      this.walletResize?.disconnect();
      this.edgeResize?.disconnect();
      this.checkoutObserver?.disconnect();
      this.checkoutObserver = null;
      this.headerResize?.disconnect();
      this.summaryResize?.disconnect();
      this.holdScroll?.abort();
      this.couponRelease?.abort();
      this.observedCheckout = null;
      document.body.classList.remove("cq-cartbar-on");
      cancelAnimationFrame(this.edgeFrame);
      cancelAnimationFrame(this.holdFrame);
      clearTimeout(this.walletTimer);
      clearInterval(this.expressHold);
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      this.refreshController?.abort();
      this.recentController?.abort();
      this.popularController?.abort();
      clearTimeout(this.popularTimer);
      clearTimeout(this.refreshTimer);
      clearTimeout(this.boldTimer);
      clearTimeout(this.toastTimer);
      cancelAnimationFrame(this.shipFrame);
      cancelAnimationFrame(this.announcementFrame);
      this.cancelPending();
      this.setBusy(false);
      this.drawerObserversReady = false;
      this.scroller = null;
      this.initialized = false;
    }

    getSectionsToRender() {
      // The shipping bar sits outside the list on both surfaces (the side cart's header row, the page's summary). The cart page
      // adds its own pieces: the recommendations and the phone checkout bar.
      const own = ["header-mini-cart-content", "header-mini-cart-footer", "CQCartExpress", "CQCartAssurance", "CQCartShip", ...(this.page ? ["CQCartRecs", "CQCartBar"] : [])];
      return [
        { id: "header", section: document.getElementById("header")?.dataset.id, selector: ".head-slot-cart-link" },
        ...own.map((id) => ({ id, section: this.sectionKey, selector: `#${id}` })),
        { id: "mobile-dock", section: document.getElementById("mobile-dock")?.dataset.id, selector: ".head-slot-cart-link" },
      ].filter((target) => {
        const root = target.section && document.getElementById(target.id);
        // Header counts that opt in with data-cq-cart-badge are updated from the cart state, so their whole section is not re-fetched.
        return root && !(target.selector === ".head-slot-cart-link" && root.querySelector("[data-cq-cart-badge]"));
      });
    }

    sectionNames() {
      return [...new Set(this.getSectionsToRender().map((target) => target.section))];
    }

    syncState() {
      this.followLoop();
      const state = this.querySelector("[data-cq-cart-state]");
      if (!state) return;
      this.itemCount = Number(state.dataset.count) || 0;
      this.totalPrice = Number(state.dataset.total) || 0;
      this.variantIds = new Set(Array.from(this.querySelectorAll("[data-variant-id]")).map((row) => row.dataset.variantId));
      this.animateLines();
      const displayCount = state.dataset.displayCount?.trim() || String(this.itemCount);
      const countLabel = this.querySelector("[data-cq-cart-count-label]");
      if (countLabel) {
        // The page heading writes its brackets into the text ("Cart (2 items)"); generated brackets would slide on screen.
        const label = state.dataset.countLabel && countLabel.hasAttribute("data-cq-brackets") ? `(${state.dataset.countLabel})` : state.dataset.countLabel;
        if (label && countLabel.textContent !== label) countLabel.textContent = label;
        // The page heading keeps "(0 items)" for an empty cart (round 9); the side cart drops the count.
        countLabel.hidden = displayCount === "0" && !this.page;
      }
      // Forward compatible: a header badge that opts in is updated here instead of re-rendering the header section.
      document.querySelectorAll("[data-cq-cart-badge]").forEach((badge) => {
        if (badge.textContent !== displayCount) badge.textContent = displayCount;
        badge.toggleAttribute("hidden", displayCount === "0");
      });
      // The header stays in place, so refresh its translated accessible name with the same cart response as its badge.
      if (state.dataset.cartName) document.querySelectorAll("[data-cq-cart-name]").forEach((name) => {
        if (name.textContent !== state.dataset.cartName) name.textContent = state.dataset.cartName;
      });
      // A footer held for a first add (holdFooter: the totals and, without a plan, the wallet row) keeps its place while
      // that add is in flight (holding the footer re-runs this through the footer's observer); the answer's cart state then
      // shows it.
      const holding = Boolean(this.pending?.footerHeld && !this.itemCount);
      this.querySelector("[data-cq-cart-bottom]")?.toggleAttribute("hidden", this.itemCount === 0);
      // Recurring plans keep the wallet row hidden (r11 item 6, r12 item 1).
      if (!holding) this.syncSubscription(state.dataset.hasRecurring === "true");
      if (this.isOpen) this.loadImages();
      this.syncShipping();
      const miniCart = this.closest("mini-cart");
      if (miniCart) miniCart.currentItemCount = this.itemCount;
      if (this.itemCount) this.recentController?.abort();
      if (this.mutating) this.setBusy(true);
      if (this.isOpen && !this.page) this.setExpanded(true);
      if (this.isOpen && !this.itemCount) this.personalizeEmptyCart();
      this.toggleAttribute("data-empty", !this.itemCount);
      if (this.itemCount) this.removeAttribute("data-cq-trust-hold");
      if (this.page) {
        // The installments line each update renders gets the summary's look before it paints (main-cart-items, round 9).
        window.CQStyleTerms?.(this);
        this.syncPopular(state);
        this.syncReassurance(state);
        this.syncPage();
      }
      // A section swap replaces the list; watch the new one so the scroll edges follow its height (adds, removes, collapses).
      if (this.edgeResize && this.edgeTarget !== state) {
        if (this.edgeTarget) this.edgeResize.unobserve(this.edgeTarget);
        this.edgeResize.observe(state);
        this.edgeTarget = state;
      }
      this.queueEdges();
    }

    open(opener) {
      if (!this.isConnected) return;
      // The page is the cart: nothing opens. An add from the page is confirmed in place (toast, live region, the new line).
      if (this.page) {
        this.personalizeEmptyCart();
        return;
      }
      this.initDrawerObservers();
      const opening = { opener };
      document.dispatchEvent(new CustomEvent("cart-drawer:before-open", { detail: opening }));
      opener = opening.opener;
      const wasOpen = this.isOpen;
      const quickView = document.querySelector("quick-view-drawer");
      if (quickView?.isOpen) {
        opener = quickView.openedBy || opener;
        this.returnFromOptions = null;
        quickView.close();
      }
      document.querySelectorAll("product-compare-modal[open]").forEach((modal) => modal.hide?.());
      if (!wasOpen && opener && !this.contains(opener)) this.openedBy = opener;
      if (!this.openedBy?.isConnected) this.openedBy = document.querySelector("#header .head-slot-cart-link");
      // Phones open the cart as the menu's sheet, from under the navy strip while it is on screen (cq-cart-drawer.css).
      if (!wasOpen) this.style.setProperty("--cq-cart-top", `${Math.max(0, document.getElementById("header")?.getBoundingClientRect().top || 0)}px`);
      this.isOpen = true;
      this.setAttribute("aria-hidden", "false");
      // r11 item 1: the shared scroll lock (assets/cq-scroll-lock.js) instead of overflow: hidden on the body, which hid the
      // page's scrollbar and left its empty gutter at the right edge.
      window.cqScrollLock?.lock(this, { root: this, isOpen: () => this.isOpen && !this.page });
      document.body.classList.add("cq-cart-open", "header-mini-cart-open", "drawer-open");
      this.setExpanded(true);
      this.loadImages();
      if (!wasOpen) {
        if (this.staleAfterAdd) {
          this.staleAfterAdd = false;
          this.scheduleRefresh(false);
        }
        this.syncShipping(true);
        this.setPageInert(true);
        this.focusFirst();
        // Some openers move focus back to themselves after their click handler; claim it once more after paint.
        cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => requestAnimationFrame(() => {
          if (this.isOpen && !this.contains(document.activeElement) && !this.modalOpen()) this.focusFirst();
        }));
        this.closest("mini-cart")?.dispatchEvent(new CustomEvent("mini-cart:opened", { bubbles: true }));
        document.dispatchEvent(new CustomEvent("cart-drawer:open"));
      }
      this.personalizeEmptyCart();
      this.announce(`${this.dataset.title} (${this.itemCount})`);
    }

    close(returnFocus = true) {
      if (!this.isOpen || this.page) return;
      this.isOpen = false;
      if (this.pending) this.pending.dismissed = true;
      cancelAnimationFrame(this.focusFrame);
      this.hideToast();
      this.returnFromOptions = null;
      this.recentController?.abort();
      this.setAttribute("aria-hidden", "true");
      this.setPageInert(false);
      this.setExpanded(false);
      window.cqScrollLock?.unlock(this);
      document.body.classList.remove("cq-cart-open", "header-mini-cart-open");
      if (!document.querySelector("quick-view-drawer")?.isOpen) document.body.classList.remove("drawer-open");
      if (typeof removeTrapFocus === "function") removeTrapFocus();
      if (returnFocus) {
        const card = this.openedBy?.closest(".cq-product-card");
        card?.classList.add("cq-card-return-focus");
        // A card's Add button sits in a hover panel that is hidden once the pointer has left. A keyboard shopper goes back
        // to the card's always-visible product link instead of the header; a mouse close keeps the header target, so a
        // focused card never holds its hover panel open.
        const keyboard = this.closedByKeyboard;
        const cardLink = keyboard ? card?.querySelector(".cq-card-name a, .js-product-link:not([tabindex='-1'])") : null;
        const candidates = [this.openedBy, cardLink, ...document.querySelectorAll("#header .head-slot-cart-link")];
        const opener = candidates.find((element) => element?.isConnected && element.getClientRects().length && getComputedStyle(element).visibility !== "hidden" && !element.closest('[aria-hidden="true"]'));
        const focusTarget = opener?.matches("button,a,[tabindex]") ? opener : opener?.querySelector("button,a");
        focusTarget?.focus({ preventScroll: true });
        requestAnimationFrame(() => card?.classList.remove("cq-card-return-focus"));
      }
      this.closest("mini-cart")?.dispatchEvent(new CustomEvent("mini-cart:closed", { bubbles: true }));
      document.dispatchEvent(new CustomEvent("cart-drawer:close"));
    }

    setExpanded(expanded) {
      document.querySelectorAll('[aria-controls="HeaderMiniCart"], drawer-toggle[for="HeaderMiniCart"] a, drawer-toggle[for="HeaderMiniCart"] button').forEach((button) => {
        button.setAttribute("aria-expanded", String(expanded));
      });
    }

    focusFirst() {
      this.focusHome()?.focus({ preventScroll: true });
    }

    // While the sheet is open the page behind it is inert (A2 D-07 2026-10-14), so focus cannot step out through an iframe
    // (express checkout buttons, the preview bar) where the keydown loop cannot see Tab, and screen readers stay inside.
    // Kept usable: the drawer, every <dialog> and <top-level-modal> (the option picker and Shopify's policy modal open over
    // the sheet), the cookie notice and the chat launcher. Only the largest branches that hold none of those are made
    // inert; branches that were inert already are left alone and stay inert after close.
    setPageInert(on) {
      if (!on) {
        this.inertBranches?.forEach((element) => { element.inert = false; });
        this.inertBranches = null;
        return;
      }
      if (this.inertBranches || this.page) return;
      const keep = new Set([this, ...document.querySelectorAll("dialog, top-level-modal, pandectes-cmp, #hubspot-messages-iframe-container")]);
      const paths = new Set();
      keep.forEach((element) => { for (let node = element.parentElement; node && !paths.has(node); node = node.parentElement) paths.add(node); });
      const branches = [];
      const walk = (parent) => {
        Array.from(parent.children).forEach((child) => {
          if (keep.has(child) || child.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(child.tagName)) return;
          if (paths.has(child)) walk(child);
          else branches.push(child);
        });
      };
      walk(document.body);
      branches.forEach((element) => { element.inert = true; });
      this.inertBranches = branches;
    }

    // Where focus goes when the control that had it disappears: the drawer's header, or the page's "Your cart" heading.
    focusHome() {
      return this.querySelector(this.page ? "[data-cq-cart-heading]" : ".drawer-header");
    }

    // The option picker is a native modal dialog in the top layer; it owns focus and Escape while open. So does Shopify's
    // cancellation policy modal (opened from the wallet consent), a <top-level-modal> element appended to the body.
    modalOpen() {
      if (Array.from(document.querySelectorAll("top-level-modal")).some((modal) => modal.getClientRects().length)) return true;
      try {
        return Boolean(document.querySelector("dialog[open]:modal"));
      } catch (_) {
        return Boolean(document.querySelector("dialog[open]"));
      }
    }

    // Line and recommendation photos are eager (low priority) in the markup. The empty state's category photos stay lazy for
    // page speed and load on intent (pointer or focus on the cart icon) or, at the latest, when the drawer opens.
    loadImages() {
      if (this.page) return;
      this.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = "eager"; });
    }

    // Section swaps replace the bar, so ease from the value the shopper last saw (zero on the first open) to the new one. The
    // fill and the riding truck (round 9) jump to that value with their transitions off, then glide to the new one; without
    // the jump the reset itself would start a transition and the glide would never show.
    syncShipping(opening = false) {
      const ship = this.querySelector("[data-cq-ship]");
      if (!ship) {
        if (this.isOpen) this.seenRatio = null;
        return;
      }
      const ratio = Math.min(1, Math.max(0, Number(ship.dataset.ratio) || 0));
      const fresh = !ship.dataset.cqDrawn;
      ship.dataset.cqDrawn = "true";
      if (!this.isOpen || (!fresh && !opening)) return;
      const from = this.seenRatio ?? 0;
      this.seenRatio = ratio;
      if (from === ratio || this.reducedMotion?.matches) return;
      ship.classList.toggle("is-celebrating", from < 1 && ratio >= 1);
      ship.classList.add("is-resetting");
      ship.style.setProperty("--cq-ship", String(from));
      ship.querySelectorAll(".cq-cart-ship-fill, .cq-cart-ship-rider").forEach((part) => getComputedStyle(part).transform);
      ship.classList.remove("is-resetting");
      cancelAnimationFrame(this.shipFrame);
      this.shipFrame = requestAnimationFrame(() => {
        this.shipFrame = requestAnimationFrame(() => ship.style.setProperty("--cq-ship", String(ratio)));
      });
    }

    observeWallets() {
      this.walletMutations?.disconnect();
      this.walletResize?.disconnect();
      const express = this.querySelector("#CQCartExpress");
      if (!express) return;
      // Only wallet additions/removals or a swapped express block matter; ignore the wallets' own inner rendering.
      this.walletMutations = new MutationObserver((records) => {
        if (records.some((record) => record.type === "childList" && record.target.matches?.("#CQCartExpress, .cq-cart-express-buttons, .dynamic-checkout__content, shopify-accelerated-checkout-cart"))) {
          this.scheduleWalletFit();
          this.placeConsent();
        }
      });
      this.walletMutations.observe(express, { childList: true, subtree: true });
      this.walletResize = new ResizeObserver(() => {
        const width = express.querySelector(".cq-cart-express-buttons")?.clientWidth || 0;
        if (width !== this.walletWidth) {
          this.walletWidth = width;
          this.scheduleWalletFit();
        }
      });
      this.walletResize.observe(express);
      this.scheduleWalletFit();
    }

    // Recurring plans hide the wallet row (r11 item 6); r12 item 1 removes the theme's subscription line. The shopper reads
    // Shopify's subscription terms at checkout. Wallets stay in the page, hidden, ready when the last recurring plan leaves.
    // A deferred plan that does not recur keeps the wallets with Shopify's consent in full.
    syncSubscription(hasRecurring = this.hasRecurring) {
      this.hasRecurring = Boolean(hasRecurring);
      const items = Boolean(this.itemCount);
      const express = this.querySelector("[data-cq-cart-express]");
      if (express) {
        const hide = !items || this.hasRecurring;
        if (express.hidden !== hide) express.hidden = hide;
        express.removeAttribute("data-cq-express-wait");
        express.removeAttribute("data-cq-express-hold");
      }
      this.placeConsent();
    }

    // A deferred plan that does not recur (a pre-order) keeps the wallets, with Shopify's paragraph shown in full where Shopify
    // put it; the page keeps it out of the flow only until this decision is made. Called again when Shopify redraws the
    // wallets. It never shows or hides anything else, so a redraw during a tap's preview leaves the preview alone.
    placeConsent() {
      const consent = this.querySelector("#CQCartExpress #shopify-buyer-consent");
      if (!consent || this.hasRecurring) return;
      consent.classList.add("cq-consent-placed");
      // Shopify's cancellation policy link is a plain span with a click handler; make it reachable by keyboard too.
      const policy = consent.querySelector("#shopify-subscription-policy-button");
      if (policy && !policy.hasAttribute("tabindex")) {
        policy.setAttribute("role", "button");
        policy.setAttribute("tabindex", "0");
      }
    }

    // r10 fix: the first add into an EMPTY side cart. The subtotal, Complete checkout and the wallet row arrived with the store's
    // answer, about a second after the tap, and the footer grew up to 214 px out of the input's grace period (layout shift
    // 0.044 at 390). With the tap the footer now takes its full height, unseen: the footer's own markup from
    // template[data-cq-footer-hold] (the same snippet, so the same height) and the wallet row's reserved row for a one-time
    // add. An add on a recurring plan keeps the wallets hidden, with no theme subscription line (r12 item 1). Release 1 lane L:
    // an empty side cart has no trust row, so it shows with the tap too (data-cq-trust-hold, cleared once the cart has items).
    // The answer replaces the copy in place and shows it all; a failed or cancelled add undoes it.
    holdFooter(pending, subscribed) {
      const footer = this.querySelector("#header-mini-cart-footer");
      const template = footer?.querySelector(":scope > template[data-cq-footer-hold]");
      if (this.itemCount || !template || footer.querySelector(".cq-cart-footer")) return;
      const hold = document.createElement("div");
      hold.className = "cq-cart-footer-hold";
      hold.setAttribute("aria-hidden", "true");
      hold.inert = true;
      hold.append(template.content.cloneNode(true));
      footer.append(hold);
      pending.footerHeld = true;
      pending.undo.push(() => hold.remove());
      this.setAttribute("data-cq-trust-hold", "");
      pending.undo.push(() => this.removeAttribute("data-cq-trust-hold"));
      const row = subscribed ? null : this.querySelector("[data-cq-cart-express]");
      if (row?.hidden) {
        row.hidden = false;
        row.setAttribute("data-cq-express-hold", "");
        pending.undo.push(() => {
          row.hidden = true;
          row.removeAttribute("data-cq-express-hold");
        });
      }
    }

    // What a change to the cart's plans will change on screen changes with the tap (round 10 item 3, r11 item 6; round 9 rule:
    // nothing may move when Shopify answers after the tap's input window). The first plan in the cart (an add on a plan, a
    // replacement cartridge from the product page, a line switched to Subscribe) hides the wallet row; the last plan leaving
    // brings the wallet row back unseen (data-cq-express-wait),
    // holding its place until Shopify's answer brings wallets drawn for a cart without a plan (syncSubscription shows them).
    // An empty side cart is left to holdFooter.
    previewSubscription(pending, subscribed) {
      if (!this.itemCount) return;
      const express = this.querySelector("[data-cq-cart-express]");
      if (subscribed) {
        this.setHidden(pending, express, true);
        return;
      }
      if (express?.hidden) {
        express.setAttribute("data-cq-express-wait", "");
        pending.undo.push(() => express.removeAttribute("data-cq-express-wait"));
        this.setHidden(pending, express, false);
      }
    }

    // Scroll edges (round 8, AJ 2026-10-08: "a weird white gradient ... its there always"): no overlay sits on the list.
    // While more list sits past an edge, the fixed part on that side draws a 1 px hairline instead (the header at the top, the
    // footer at the bottom). Scroll work is batched to one frame. Round 9: the shipping bar lives in the header row, so it is
    // never pinned inside the list any more.
    observeScroll() {
      const scroller = this.querySelector(".cq-cart-content");
      if (!scroller) return;
      this.scroller = scroller;
      scroller.addEventListener("scroll", () => this.queueEdges(), { passive: true, signal: this.listeners.signal });
      this.edgeResize = new ResizeObserver(() => this.queueEdges());
      this.edgeResize.observe(scroller);
    }

    // Cart page (round 6). The header cart icon would open a drawer that is not on this page: it brings the shopper to the
    // top of the cart instead. Below 990 px a compact checkout bar (subtotal and Complete checkout) shows whenever the main
    // Complete checkout button is out of view. From 990 px the order summary is sticky; when it is taller than the screen it
    // sticks by its bottom edge instead, so every part of it stays reachable.
    initPage() {
      const signal = this.listeners.signal;
      document.addEventListener("click", (event) => {
        const toggle = event.target.closest?.('drawer-toggle[for="HeaderMiniCart"] a, drawer-toggle[for="HeaderMiniCart"] button, [aria-controls="HeaderMiniCart"]');
        if (!toggle) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const heading = this.focusHome();
        heading?.scrollIntoView({ block: "center", behavior: this.reducedMotion?.matches ? "auto" : "smooth" });
        heading?.focus({ preventScroll: true });
      }, { capture: true, signal });
      this.barQuery = window.matchMedia("(max-width: 989px)");
      this.barQuery.addEventListener("change", () => this.syncBar(), { signal });
      this.observeCheckout();
      // The sticky navigation bar changes height (compact once scrolled); the checkout button counts as in view only below it.
      const header = document.getElementById("header");
      if (header) {
        this.headerResize = new ResizeObserver(() => this.observeCheckout());
        this.headerResize.observe(header);
      }
      const card = this.querySelector("[data-cq-summary-card]");
      if (card) {
        this.summaryResize = new ResizeObserver(() => this.syncSummaryTop());
        this.summaryResize.observe(card);
        window.addEventListener("resize", () => this.syncSummaryTop(), { passive: true, signal });
      }
      this.syncPage();
    }

    // The main Complete checkout button counts as in view when at least half of it shows below the sticky navigation bar; a
    // button tucked under that bar is out of view, so the phone checkout bar shows (round 6 check: after an add the button sat
    // at -26 to 22 px, under the 60 px bar, and neither button was visible).
    observeCheckout() {
      const header = document.getElementById("header");
      const position = header ? getComputedStyle(header).position : "";
      const top = position === "sticky" || position === "fixed" ? Math.ceil(header.getBoundingClientRect().height) : 0;
      if (this.checkoutObserver && top === this.checkoutMargin) return;
      this.checkoutMargin = top;
      this.checkoutObserver?.disconnect();
      this.checkoutObserver = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.checkoutVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        this.syncBar();
      }, { rootMargin: `-${top}px 0px 0px 0px`, threshold: [0, 0.5, 1] });
      if (this.observedCheckout) this.checkoutObserver.observe(this.observedCheckout);
    }

    // For an add: the container to keep still. An add from "Enhance your setup" keeps the suggestions where the shopper is
    // looking (the summary above them may grow); an add from "Popular right now" (round 9, passed as from) keeps that row.
    // Otherwise, while the top of the list (where the new line lands) is above the screen, the first container below it that
    // is on screen. Chrome counts a layout shift for anything on screen that moves at all, so the scroll is corrected once,
    // after every part of the update has landed (renderContents).
    pageAnchor(fromRecs = false, from = null) {
      const visible = (element) => element?.getClientRects().length && element.getBoundingClientRect().bottom > 0 && element.getBoundingClientRect().top < window.innerHeight;
      const source = from || (fromRecs ? this.querySelector("#CQCartRecs") : null);
      let element = visible(source) ? source : null;
      if (!element) {
        const items = this.querySelector(".cq-cartpage-items");
        if (!items || items.getBoundingClientRect().top >= 0) return null;
        const candidates = this.barQuery?.matches ? [".cq-cartpage-summary", "#CQCartRecs", "#CQCartPopular"] : ["#CQCartRecs", "#CQCartPopular"];
        element = candidates.map((selector) => this.querySelector(selector)).find((candidate) => candidate?.getClientRects().length && candidate.getBoundingClientRect().bottom > 0);
      }
      return element ? { element, top: element.getBoundingClientRect().top, tail: this.pageTail(element) } : null;
    }

    keepAnchor(anchor) {
      if (!anchor?.element.isConnected) return;
      const delta = anchor.element.getBoundingClientRect().top - anchor.top;
      if (Math.abs(delta) >= 0.5) window.scrollBy({ top: delta, behavior: "instant" });
    }

    // From the anchor's top to the end of the cart page. When an update shortens it (a suggestion leaves, or on desktop the
    // line list grows beside a taller summary), the footer under it would move on screen; a spacer at the end of the page holds
    // that distance and is let go only where the change cannot be seen (holdTail).
    pageTail(element) {
      const end = this.querySelector("[data-cq-page-hold]");
      return end ? end.getBoundingClientRect().bottom - element.getBoundingClientRect().top : 0;
    }

    holdTail(anchor) {
      const spacer = this.querySelector("[data-cq-page-hold]");
      if (!spacer || !anchor?.element.isConnected || !anchor.tail) return;
      const short = anchor.tail - this.pageTail(anchor.element);
      const hold = Math.min(1600, Math.max(0, Math.round((this.pageHold || 0) + short)));
      if (hold === (this.pageHold || 0)) return;
      this.pageHold = hold;
      spacer.style.height = hold ? `${hold}px` : "";
      if (hold && !this.holdScroll) {
        this.holdScroll = new AbortController();
        const check = () => {
          cancelAnimationFrame(this.holdFrame);
          this.holdFrame = requestAnimationFrame(() => this.releaseHold());
        };
        window.addEventListener("scroll", check, { passive: true, signal: this.holdScroll.signal });
        window.addEventListener("resize", check, { passive: true, signal: this.holdScroll.signal });
      }
      if (!hold) this.releaseHold(true);
    }

    // Let the spacer go once it is entirely below the screen (nothing on screen moves) or entirely above it (the scroll is
    // corrected by the same amount in the same task, so the footer on screen stays put).
    releaseHold(force = false) {
      const spacer = this.querySelector("[data-cq-page-hold]");
      if (spacer && this.pageHold) {
        const rect = spacer.getBoundingClientRect();
        const below = rect.top >= window.innerHeight;
        const above = rect.bottom <= 0;
        if (!force && !below && !above) return;
        const before = rect.bottom;
        spacer.style.height = "";
        this.pageHold = 0;
        if (above) {
          const delta = spacer.getBoundingClientRect().bottom - before;
          if (Math.abs(delta) >= 0.5) window.scrollBy({ top: delta, behavior: "instant" });
        }
      }
      this.pageHold = 0;
      this.holdScroll?.abort();
      this.holdScroll = null;
    }

    // The reassurance band is not re-rendered with the cart; its shipping card shows the free-shipping sentence only while the
    // cart qualifies (the tracker's rule), else the neutral one, so adding or removing a media or commercial item updates it.
    syncReassurance(state) {
      if (!state?.dataset.shippingEligible) return;
      const eligible = state.dataset.shippingEligible === "true";
      this.querySelectorAll("[data-cq-reassure-ship]").forEach((sentence) => {
        const hide = (sentence.dataset.cqReassureShip === "free") !== eligible;
        if (sentence.hidden !== hide) sentence.hidden = hide;
      });
    }

    syncPage() {
      // Totals are announced politely when they change (the summary itself is re-rendered, so it cannot be the live region).
      const subtotal = this.querySelector(".cq-cart-footer [data-cq-subtotal]")?.textContent.trim() || "";
      const totalLive = this.querySelector("[data-cq-total-live]");
      if (totalLive && this.announcedTotal !== undefined && subtotal && subtotal !== this.announcedTotal) {
        totalLive.textContent = `${this.dataset.subtotalLabel || ""} ${subtotal}`.trim();
      }
      this.announcedTotal = subtotal;
      const checkout = this.querySelector(".cq-cart-checkout");
      if (checkout !== this.observedCheckout) {
        if (this.observedCheckout) this.checkoutObserver?.unobserve(this.observedCheckout);
        this.observedCheckout = checkout;
        // Until the observer reports, assume the button is in view so the bar never flashes on load.
        if (checkout) this.checkoutObserver?.observe(checkout);
        else this.checkoutVisible = true;
      }
      this.syncBar();
    }

    syncBar() {
      const bar = this.querySelector("[data-cq-page-bar]");
      if (!bar) return;
      // An empty layout taken with the tap that removes the last line (emptyPage) hides it too, before the count catches up.
      const show = Boolean(this.barQuery?.matches && this.itemCount && !this.hasAttribute("data-empty") && this.observedCheckout && this.checkoutVisible === false);
      // Hidden, the bar is visibility: hidden (CSS), so it is out of the tab order and the accessibility tree.
      if (bar.hasAttribute("data-visible") === show) return;
      bar.toggleAttribute("data-visible", show);
      document.body.classList.toggle("cq-cartbar-on", show);
    }

    syncSummaryTop() {
      const card = this.querySelector("[data-cq-summary-card]");
      if (!card) return;
      const offset = parseFloat(getComputedStyle(this).getPropertyValue("--cq-summary-offset")) || 24;
      const top = Math.round(Math.min(offset, window.innerHeight - card.offsetHeight - 16));
      if (top !== this.summaryTop) {
        this.summaryTop = top;
        this.style.setProperty("--cq-summary-top", `${top}px`);
      }
    }

    queueEdges() {
      if (this.edgeFrame) return;
      this.edgeFrame = requestAnimationFrame(() => {
        this.edgeFrame = null;
        this.updateEdges();
      });
    }

    updateEdges() {
      const scroller = this.scroller;
      const panel = scroller?.closest(".cq-cart-panel");
      if (!scroller?.isConnected || !panel) return;
      const room = scroller.scrollHeight - scroller.clientHeight;
      panel.toggleAttribute("data-edge-top", room > 1 && scroller.scrollTop > 1);
      panel.toggleAttribute("data-edge-bottom", room > 1 && room - scroller.scrollTop > 1);
    }

    holdExpress(root) {
      const height = root.offsetHeight;
      clearInterval(this.expressHold);
      if (!height) return;
      root.style.height = `${height}px`;
      root.style.overflow = "hidden";
      let settled = 0;
      let checks = 0;
      this.expressHold = setInterval(() => {
        checks += 1;
        // Settled: the wallet buttons are drawn, sit side by side, and fit the held height.
        const checkout = root.querySelector("shopify-accelerated-checkout-cart");
        const tops = checkout ? Array.from(checkout.children).filter((child) => child.getBoundingClientRect().height > 0).map((child) => Math.round(child.getBoundingClientRect().top)) : [];
        const inRow = tops.length > 0 && tops.every((top) => Math.abs(top - tops[0]) < 2);
        const fits = (root.querySelector(".cq-cart-express-buttons")?.scrollHeight || 0) <= height;
        settled = (inRow && fits) || !checkout ? settled + 1 : 0;
        if (settled >= 2 || checks >= 25 || !root.isConnected) {
          clearInterval(this.expressHold);
          root.style.height = "";
          root.style.overflow = "";
        }
      }, 160);
    }

    scheduleWalletFit() {
      clearTimeout(this.walletTimer);
      this.walletTimer = setTimeout(() => this.fitWallets(), 200);
    }

    // Shopify's cart wallets form one row only when every enabled wallet fits side by side (about 102 px each: Shop Pay,
    // Amazon Pay, PayPal, Google Pay, plus Apple Pay on Safari); otherwise it stacks them. When they would stack, give the
    // row the width it needs and scale it to the footer. Button height comes from the documented custom property in CSS.
    async fitWallets() {
      const wrap = this.querySelector(".cq-cart-express-buttons");
      const checkout = wrap?.querySelector("shopify-accelerated-checkout-cart");
      if (!wrap || !checkout || !wrap.getClientRects().length) return;
      const token = (this.walletToken || 0) + 1;
      this.walletToken = token;
      const available = wrap.clientWidth;
      const buttons = () => Array.from(checkout.children).filter((child) => child.getBoundingClientRect().height > 0);
      const inRow = () => {
        const tops = buttons().map((button) => Math.round(button.getBoundingClientRect().top));
        return tops.length > 0 && tops.every((top) => Math.abs(top - tops[0]) < 2);
      };
      const waitForRow = async () => {
        for (let i = 0; i < 15; i += 1) {
          if (inRow()) return true;
          await new Promise((resolve) => setTimeout(resolve, 100));
          if (token !== this.walletToken) return false;
        }
        return inRow();
      };
      const count = buttons().length;
      if (!count) return;
      const fitted = checkout.hasAttribute("data-cq-fitted");
      if (!fitted && inRow()) return;
      if (fitted && checkout.dataset.cqCount === String(count) && available === this.walletFitWidth && inRow()) return;
      for (const width of [count * 110, (count + 1) * 110]) {
        if (width <= available && !fitted) return;
        const scale = Math.min(1, available / width);
        checkout.setAttribute("data-cq-fitted", "");
        checkout.dataset.cqCount = String(count);
        checkout.style.width = `${width}px`;
        checkout.style.transform = `scale(${scale.toFixed(4)})`;
        // Side cart (round 9): the row lands at 40 px on screen whatever the scale (measured 32 px with four wallets at 320).
        if (!this.page) checkout.style.setProperty("--shopify-accelerated-checkout-button-inline-size", `${Math.round(40 / scale)}px`);
        this.walletFitWidth = available;
        if (await waitForRow()) {
          wrap.style.height = `${Math.ceil(checkout.offsetHeight * scale)}px`;
          return;
        }
        if (token !== this.walletToken) return;
      }
    }

    showToast() {
      const toast = this.querySelector("[data-cq-cart-toast]");
      if (!toast) return;
      clearTimeout(this.toastTimer);
      toast.classList.remove("is-visible");
      void toast.offsetWidth;
      toast.classList.add("is-visible");
      this.toastTimer = setTimeout(() => this.hideToast(), 2600);
    }

    hideToast() {
      clearTimeout(this.toastTimer);
      this.querySelector("[data-cq-cart-toast]")?.classList.remove("is-visible");
    }

    focusableElements(root = this) {
      const elements = [];
      root.querySelectorAll("*").forEach((element) => {
        let closedDetails = element.closest("details:not([open])");
        while (closedDetails) {
          if (closedDetails !== element && !closedDetails.querySelector(":scope > summary")?.contains(element)) return;
          closedDetails = closedDetails.parentElement?.closest("details:not([open])");
        }
        if (element.matches("a[href],button:not(:disabled),input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),summary,iframe,[tabindex]") && !element.matches(":disabled") && element.tabIndex >= 0 && !element.closest('[inert], [aria-hidden="true"]') && element.getClientRects().length && getComputedStyle(element).visibility !== "hidden") elements.push(element);
        // Shopify's native express controls live in web-component shadow roots.
        if (element.shadowRoot) elements.push(...this.focusableElements(element.shadowRoot));
      });
      return elements;
    }

    activeElement() {
      let active = document.activeElement;
      while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
      return active;
    }

    onKeydown(event) {
      if (!this.isOpen || this.page || this.modalOpen()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closedByKeyboard = true;
        this.close();
        this.closedByKeyboard = false;
      } else if (event.key === "Tab") {
        // Recompute after each section swap; collapsed discount controls must not trap focus.
        const focusable = this.focusableElements();
        const active = this.activeElement();
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) {
          event.preventDefault();
          this.focusFirst();
          return;
        }
        // A closed Shopify shadow root retargets focus to its host; let its native controls tab normally.
        if (event.shiftKey && (active === first || active === this.querySelector(".drawer-header") || !this.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !this.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
        event.stopImmediatePropagation();
      }
    }

    onClick(event) {
      // A focused control kept enabled while the cart is busy (setBusy) does nothing until Shopify answers.
      const unavailable = this.mutating ? event.target.closest?.('[aria-disabled="true"]') : null;
      if (unavailable && this.softDisabled.has(unavailable)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (event.target.closest("[data-cq-cart-close]")) {
        event.preventDefault();
        // A click with no pointer detail came from Enter or Space on the close button.
        this.closedByKeyboard = event.detail === 0;
        this.close();
        this.closedByKeyboard = false;
        return;
      }
      const options = event.target.closest("[data-cq-cross-options]");
      if (options?.closest("drawer-toggle") && document.getElementById(options.closest("drawer-toggle").getAttribute("for"))) {
        this.close(false);
        this.returnFromOptions = options.closest("[data-cross-sell-id]")?.dataset.crossSellId;
        return;
      }
      const remove = event.target.closest("[data-cq-remove]");
      const stepper = event.target.closest("[data-cq-quantity]");
      if (remove || stepper) {
        event.preventDefault();
        const row = (remove || stepper).closest("[data-key]");
        const input = row?.querySelector("[data-cq-quantity-input]");
        if (!input) return;
        const step = Number(input.step) || 1;
        let next = remove ? 0 : Number(input.value) + (stepper.dataset.cqQuantity === "plus" ? step : -step);
        if (stepper?.dataset.cqQuantity === "minus" && next < Number(input.min)) next = 0;
        this.changeQuantity(row, next);
      }
      const finder = event.target.closest("[data-cq-find-system]");
      // A modified click (new tab or window) keeps the plain link to the quiz.
      if (finder && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) && this.openAssistant()) event.preventDefault();
      if (event.target.closest("[data-cq-apply-coupon]")) this.applyCoupon();
      const policy = event.target.closest("#shopify-subscription-policy-button");
      if (policy) this.openSubscriptionPolicy(policy);
    }

    // Find my system (round 8, AJ 2026-10-08: "the find my system would be way cooler if it opened an ai agent"): it opens the
    // site's chat assistant, Quest AI (HubSpot conversations), once that widget has loaded. Nothing is typed or sent for the
    // shopper. The side cart closes first (focus goes back to where the cart was opened, then the chat takes it); the chat
    // launcher is hidden only while the cart is open. Returns false when the assistant is not available, so the link goes to
    // the recommendation quiz instead.
    openAssistant() {
      const widget = window.HubSpotConversations?.widget;
      let loaded = false;
      try {
        loaded = Boolean(widget?.status?.()?.loaded) && typeof widget.open === "function";
      } catch (_) {
        loaded = false;
      }
      if (!loaded) return false;
      if (!this.page) this.close();
      try {
        widget.open();
        return true;
      } catch (_) {
        return false;
      }
    }

    // Shopify binds its cancellation policy modal to the FIRST element with this id (Shopify.PaymentButton.showBuyerConsent
    // uses getElementById). A product page renders its own wallet consent earlier in the page, so there the drawer's copy of
    // the link gets no handler and the click would do nothing; hand it to the bound link instead. Elsewhere the drawer's link
    // is the first one and Shopify's own handler runs. The modal returns focus to the drawer's link on close.
    openSubscriptionPolicy(policy) {
      const bound = document.getElementById(policy.id);
      if (!bound || bound === policy) return;
      bound.click();
    }

    // Optimistic add (AJ 2026-10-08, "add to cart is a bit slow"): Shopify's cart mutation alone takes 350 to 900 ms on this
    // store, so the drawer opens on the click with the product it is adding, and the real line replaces it when /cart/add
    // answers with the bundled drawer section. Called by product-form.js; returns { done(cart), fail(message), dismissed }.
    beginAdd({ opener, form, extras = [] } = {}) {
      if (!this.isConnected) return null;
      this.cancelPending();
      const wasOpen = this.isOpen;
      const preview = this.addPreview(opener, form);
      const variantId = String(form?.elements?.id?.value || "");
      const planInput = form?.elements?.selling_plan;
      const plan = planInput && !planInput.disabled ? planInput.value : "";
      const state = this.querySelector("[data-cq-cart-state]");
      const empty = this.querySelector(".cq-cart-empty");
      let lines = this.querySelector(".cq-cart-lines");
      const existing = Array.from(this.querySelectorAll(".cq-cart-line[data-key]")).find((row) => row.dataset.variantId === variantId && (row.dataset.subscribed === "true") === Boolean(plan));
      // A first item added on the empty cart page (round 9 check, from "Popular right now" or any other Add on the page):
      // nothing on the page changes with the tap (the tapped Add shows its spinner and the live region says "Adding"); Shopify's
      // answer then replaces the whole page at once with the filled cart, shown from its top (swapFreshPage). Inserting the
      // summary and "Enhance your setup" above the tapped row and scrolling to keep that row still moved the row's container
      // on screen (layout shift 0.703 at 390 px and 0.361 at 768 px).
      const freshPage = this.page && this.hasAttribute("data-empty");
      const offer = this.page && !freshPage ? opener?.closest?.("#CQCartRecs .cq-cart-offer") : null;
      const popular = this.page && !freshPage ? opener?.closest?.("#CQCartPopular .cq-cart-offer") : null;
      const pending = { existing, empty: !freshPage && empty && !empty.hidden ? empty : null, row: null, lines: null, form, dismissed: false, fromRecs: Boolean(offer), anchorFrom: popular ? this.querySelector("#CQCartPopular") : null, freshPage, undo: [] };
      const anchor = this.page && !freshPage ? this.pageAnchor(pending.fromRecs, pending.anchorFrom) : null;
      if (existing) {
        existing.classList.add("is-pending");
      } else if (state && !freshPage) {
        if (!lines) {
          lines = document.createElement("div");
          lines.className = "cq-cart-lines";
          lines.setAttribute("role", "list");
          state.insertBefore(lines, empty || null);
          pending.lines = lines;
        }
        pending.empty?.setAttribute("hidden", "");
        pending.row = this.pendingRow(preview);
        lines.prepend(pending.row);
      }
      // r10 item 13: the replacement subscription added with the product: its line, when the cart already holds it on the
      // same plan, shows the spinner (Shopify adds one to it); else it gets its own placeholder line at the top, where Shopify
      // puts it (the newest line comes first, and the cartridge is sent after the product, so it lands above it).
      pending.extraRows = [];
      pending.extraExisting = [];
      if (state && !freshPage && extras.length) {
        const rows = Array.from(this.querySelectorAll(".cq-cart-line[data-key]"));
        extras.forEach((extra) => {
          const line = rows.find((row) => row.dataset.variantId === String(extra.id) && (row.dataset.subscribed === "true") === Boolean(extra.plan));
          if (line) {
            line.classList.add("is-pending");
            pending.extraExisting.push(line);
          } else pending.extraRows.push(this.pendingRow({ title: extra.title, src: extra.src }));
        });
        if (pending.extraRows.length) this.querySelector(".cq-cart-lines")?.prepend(...pending.extraRows);
      }
      if (this.page && !freshPage) {
        // What the update will change on screen changes now, with the tap: the suggestion leaves the list (Shopify's answer no
        // longer offers what is in the cart) and the subscription line shows for a plan. The answer then matches the page.
        if (offer) {
          this.hideForAdd(pending, offer);
          const section = offer.closest(".cq-cart-cross-sells");
          if (section && !section.querySelector(".cq-cart-offer:not([hidden])")) this.hideForAdd(pending, section);
          // Keyboard focus was on the tapped Add; it goes to the next suggestion's Add, or to the cart heading.
          const next = section?.querySelector(".cq-cart-offer:not([hidden]) .cq-cart-offer-action");
          if (opener && opener === document.activeElement) {
            pending.refocusId = next?.closest("[data-cross-sell-id]")?.dataset.crossSellId || "";
            (next || this.focusHome())?.focus({ preventScroll: true });
          }
        }
        // "Popular right now" (round 9): the next spare card takes the tapped card's slot, so the row keeps its size.
        if (popular) this.swapPopular(pending, popular, opener);
        this.previewSavings(pending, Number((offer || popular)?.dataset.cqSaving) || 0);
        this.keepAnchor(anchor);
      }
      const subscribed = Boolean(plan || extras.some((extra) => extra.plan));
      if (!this.page) this.holdFooter(pending, subscribed);
      if (subscribed && !freshPage) this.previewSubscription(pending, true);
      this.pending = pending;
      this.pendingPreview = Object.assign(preview, { fromEmpty: Boolean(pending.lines) });
      this.setAttribute("data-loading", "");
      this.querySelector(".cq-cart-content")?.setAttribute("aria-busy", "true");
      this.open(opener);
      // Show where the new line lands. Adding from a recommendation inside the open drawer glides there instead of jumping.
      const scroller = this.querySelector(".cq-cart-content");
      const behavior = wasOpen && !this.reducedMotion?.matches ? "smooth" : "auto";
      if (this.page) {
        // The page stays where the shopper is; the toast and the live region confirm the add.
      } else if (existing) existing.scrollIntoView({ block: "nearest", behavior });
      else scroller?.scrollTo({ top: 0, behavior });
      this.announce(this.dataset.adding);
      return {
        done: (cart) => this.endAdd(pending, true, null, cart),
        fail: (message) => this.endAdd(pending, false, message),
        get dismissed() { return pending.dismissed; },
      };
    }

    endAdd(pending, ok, message, cart) {
      if (this.pending !== pending) return;
      this.pending = null;
      this.removeAttribute("data-loading");
      if (!this.mutating) this.querySelector(".cq-cart-content")?.setAttribute("aria-busy", "false");
      pending.existing?.classList.remove("is-pending");
      pending.extraExisting?.forEach((row) => row.classList.remove("is-pending"));
      if (ok) {
        // The shopper closed the cart while it was adding: the badge updates, but the drawer is not pushed back open.
        if (pending.dismissed && cart && typeof cart === "object") (this.quietCarts ||= new WeakSet()).add(cart);
        // No drawer HTML came back with the add: the follow-up refresh replaces the placeholder, or clears it if it fails.
        if (pending.row?.isConnected) this.orphanPending = pending;
        return;
      }
      // Nothing was added: put the drawer back as it was and say why, inside the open dialog.
      pending.row?.remove();
      pending.extraRows?.forEach((row) => row.remove());
      pending.lines?.remove();
      pending.empty?.removeAttribute("hidden");
      this.undoForAdd(pending);
      this.pendingPreview = null;
      this.lineError(null, message || cartStrings.error);
      // A form the shopper can still see shows the message itself; one inside the closed configure popup or a card's hidden
      // hover panel cannot, so the cart reopens to say why nothing was added.
      const form = pending.form;
      const formVisible = Boolean(form?.isConnected && form.getClientRects().length && getComputedStyle(form).visibility !== "hidden");
      if (pending.dismissed && !formVisible) this.open();
    }

    // The add itself succeeded, so the product stays listed (photo and name); only the spinner stops, next to the error.
    clearOrphanPending() {
      const orphan = this.orphanPending;
      this.orphanPending = null;
      orphan?.row?.querySelector(".cq-cart-pending-status")?.remove();
      orphan?.extraRows?.forEach((row) => row.querySelector(".cq-cart-pending-status")?.remove());
    }

    cancelPending() {
      if (!this.pending) return;
      const pending = this.pending;
      this.pending = null;
      pending.row?.remove();
      pending.extraRows?.forEach((row) => row.remove());
      pending.lines?.remove();
      pending.empty?.removeAttribute("hidden");
      pending.existing?.classList.remove("is-pending");
      pending.extraExisting?.forEach((row) => row.classList.remove("is-pending"));
      this.undoForAdd(pending);
      this.removeAttribute("data-loading");
    }

    // "Popular right now" on the cart page (round 9). The page renders the store's best sellers in the row and holds its space
    // (data-cq-popular-wait); this asks Shopify for the related products of the first cart item (sections/cq-cart-popular,
    // the same cards and count) and puts that row in before the row shows. After 3 s, or if the answer fails, the best
    // sellers show. The row is not re-rendered with the cart; syncPopular keeps it free of what is in the cart.
    async loadPopular() {
      const box = this.querySelector("#CQCartPopular");
      if (!box?.hasAttribute("data-cq-popular-wait")) return;
      const release = () => {
        clearTimeout(this.popularTimer);
        box.removeAttribute("data-cq-popular-wait");
        this.syncPopular();
      };
      this.popularTimer = setTimeout(() => {
        this.popularController?.abort();
        release();
      }, 3000);
      const controller = new AbortController();
      this.popularController = controller;
      try {
        const url = new URL(box.dataset.url || "/recommendations/products", location.origin);
        url.searchParams.set("section_id", "cq-cart-popular");
        url.searchParams.set("product_id", box.dataset.productId);
        url.searchParams.set("limit", "10");
        url.searchParams.set("intent", "related");
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const row = doc.querySelector("[data-cq-popular-content] [data-cq-popular-row]");
        // Swapped only while nobody is using the row (the shopper cannot see it yet, but keyboard focus could be in it).
        if (row && box.hasAttribute("data-cq-popular-wait") && !box.contains(document.activeElement) && !this.pending) { box.replaceChildren(row); this.followLoop(); }
      } catch (_) {
        // The best-seller row stays.
      } finally {
        if (this.popularController === controller) this.popularController = null;
        release();
      }
    }

    // Product ids the popular row must not show: what is in the cart and what "Enhance your setup" offers.
    popularTaken(state = this.querySelector("[data-cq-cart-state]"), extra = []) {
      const ids = (state?.dataset.productIds || "").split(",").filter(Boolean);
      this.querySelectorAll("#CQCartRecs [data-cross-sell-id]").forEach((card) => ids.push(card.dataset.crossSellId));
      return new Set([...ids, ...extra]);
    }

    // The next spare card (kept in the row's template) that is not taken, or null.
    takeSpare(row, taken) {
      const spares = row?.querySelector("template[data-cq-popular-spares]");
      if (!spares) return null;
      for (const card of Array.from(spares.content.children)) {
        card.remove();
        if (!taken.has(card.dataset.crossSellId)) return card;
      }
      return null;
    }

    // After every cart update: a card whose product is now in the cart or in "Enhance your setup" hands its slot to a spare.
    syncPopular(state) {
      const row = this.querySelector("#CQCartPopular [data-cq-popular-row]");
      if (!row) return;
      const taken = this.popularTaken(state);
      row.querySelectorAll(".cq-cart-offers > .cq-cart-offer:not([hidden])").forEach((card) => {
        if (!taken.has(card.dataset.crossSellId)) return;
        const spare = this.takeSpare(row, taken);
        if (spare) card.replaceWith(spare);
        else card.hidden = true;
      });
    }

    // An Add in the popular row: the tapped card leaves with the tap and the next spare takes its slot (put back if the add
    // fails). Keyboard focus moves to the new card's button.
    swapPopular(pending, card, opener) {
      const row = card.closest("[data-cq-popular-row]");
      const spare = this.takeSpare(row, this.popularTaken(undefined, [card.dataset.crossSellId]));
      const hadFocus = opener && opener === document.activeElement;
      if (spare) {
        card.replaceWith(spare);
        pending.undo.push(() => {
          spare.replaceWith(card);
          row?.querySelector("template[data-cq-popular-spares]")?.content.prepend(spare);
        });
      } else {
        this.hideForAdd(pending, card);
      }
      if (hadFocus) {
        const next = (spare || row?.querySelector(".cq-cart-offer:not([hidden])"))?.querySelector(".cq-cart-offer-action");
        (next || this.focusHome())?.focus({ preventScroll: true });
      }
    }

    hideForAdd(pending, element) {
      if (element.hidden) return;
      element.hidden = true;
      pending.undo.push(() => { element.hidden = false; });
    }

    // The summary's savings row with the tap (the suggestion's saving is in its data-cq-saving, by the footer's rule).
    previewSavings(pending, saving) {
      if (!saving) return;
      const row = this.querySelector("[data-cq-savings]");
      if (row) {
        const amount = row.querySelector("[data-cq-savings-amount]");
        if (!amount) return;
        const before = amount.textContent;
        amount.textContent = `\u2212${this.money((Number(row.dataset.cqSavings) || 0) + saving, before.replace("\u2212", ""))}`;
        pending.undo.push(() => { amount.textContent = before; });
        return;
      }
      const subtotal = this.querySelector(".cq-cart-footer .cq-cart-subtotal");
      if (!subtotal) return;
      const preview = document.createElement("p");
      preview.className = "cq-cart-total-savings";
      const label = document.createElement("span");
      label.textContent = this.dataset.savingsLabel || "";
      const amount = document.createElement("span");
      amount.textContent = `\u2212${this.money(saving, subtotal.querySelector("[data-cq-subtotal]")?.textContent)}`;
      preview.append(label, amount);
      // The page sets the savings line above the subtotal (round 9), where the re-rendered summary will have it.
      subtotal.before(preview);
      pending.undo.push(() => preview.remove());
    }

    undoForAdd(pending) {
      pending.undo?.reverse().forEach((undo) => undo());
      pending.undo = [];
    }

    // What is being added, read from where the shopper clicked: a product card, the product page, the configure popup or a
    // recommendation card. Missing pieces fall back to a quiet skeleton.
    addPreview(opener, form) {
      const scope = (opener || form)?.closest?.(".cq-cart-offer, .cq-product-card, .card-product, dialog, .shopify-section");
      if (!scope) return {};
      const title = scope.querySelector(".cq-cart-offer-title, .cq-card-name, .cq-configure-title, .product-info-heading, h1")?.textContent.replace(/\s+/g, " ").trim();
      // The photo the shopper is looking at: the selected slide or main image first, then any loaded product photo.
      const usable = (img) => img.complete && img.naturalWidth > 0 && img.getClientRects().length;
      const image = [
        ".cq-cart-offer-image img",
        "img.cq-card-img:not(.cq-card-img-alt)",
        '.cq-configure-slide[aria-hidden="false"] img',
        ".product-media-main.is-active img",
        "cq-configure-gallery img, media-gallery img, .product-media img",
      ].reduce((found, selector) => found || Array.from(scope.querySelectorAll(selector)).find(usable), null);
      const white = Boolean(image?.closest(".cq-img-on-white, .cq-media-on-white"));
      return { title: title?.slice(0, 120), src: image?.currentSrc || image?.src || "", white };
    }

    pendingRow({ title, src, white } = {}) {
      const row = document.createElement("div");
      row.className = "cq-cart-line cq-cart-line--pending";
      row.dataset.cqPending = "";
      row.setAttribute("aria-hidden", "true");
      const tile = document.createElement("span");
      tile.className = "cq-cart-line-image";
      if (src) {
        const img = document.createElement("img");
        img.src = src;
        img.alt = "";
        img.width = 72;
        img.height = 72;
        if (white) img.className = "cq-img-on-white";
        tile.append(img);
      } else {
        tile.classList.add("cq-cart-skel");
      }
      const info = document.createElement("div");
      info.className = "cq-cart-line-info";
      const name = document.createElement("p");
      name.className = "cq-cart-line-title";
      if (title) name.textContent = title;
      else name.append(Object.assign(document.createElement("span"), { className: "cq-cart-skel cq-cart-skel-text" }));
      const status = document.createElement("p");
      status.className = "cq-cart-pending-status";
      status.append(Object.assign(document.createElement("span"), { className: "spinner-xs" }), Object.assign(document.createElement("span"), { textContent: this.dataset.adding || "" }));
      info.append(name, status);
      row.append(tile, info);
      return row;
    }

    // The real line's photo is a different size from the card's, so it may still be loading: show the card's photo behind it.
    bridgeImages(previousKeys, preview) {
      if (!previousKeys) return;
      this.querySelectorAll(".cq-cart-line[data-key]").forEach((row) => {
        if (previousKeys.has(row.dataset.key)) return;
        const tile = row.querySelector(".cq-cart-line-image");
        const img = tile?.querySelector("img");
        if (!img || (img.complete && img.naturalWidth)) return;
        tile.style.backgroundImage = `url(${JSON.stringify(preview.src)})`;
        tile.classList.add("is-bridged");
        if (preview.white) tile.classList.add("is-bridged-white");
        const clear = () => { tile.style.backgroundImage = ""; tile.classList.remove("is-bridged", "is-bridged-white"); };
        img.addEventListener("load", clear, { once: true });
        img.addEventListener("error", clear, { once: true });
      });
    }

    // Formats like the price it replaces: a total shown with its currency code ("$64.95 USD") keeps it, so the optimistic
    // value and the server's value have the same shape and nothing shifts when the real one arrives.
    money(cents, like = "") {
      const text = new Intl.NumberFormat(document.documentElement.lang || "en", { style: "currency", currency: this.dataset.currency }).format(cents / 100);
      return /\s[A-Z]{3}$/.test(String(like).trim()) ? `${text} ${this.dataset.currency}` : text;
    }

    setBusy(busy) {
      if (busy && !this.mutating) {
        this.revision += 1;
        this.refreshController?.abort();
      }
      this.mutating = busy;
      this.querySelector(".cq-cart-content")?.setAttribute("aria-busy", String(busy));
      if (busy) {
        const focused = this.activeElement();
        this.querySelectorAll("[data-cq-quantity], [data-cq-quantity-input], [data-cq-remove], [data-cq-apply-coupon], #CQCartCoupon, .cq-cart-checkout, #CQCartConsent, [data-cq-plan-mode], [data-cq-line-plan-select], product-form [type=submit]").forEach((control) => {
          if (this.disabledControls.has(control) || this.softDisabled.has(control)) return;
          // The control that has focus (the +/- just pressed, for example) is marked unavailable instead of disabled: a disabled
          // control drops keyboard focus to the page until Shopify answers. Its actions are ignored while the cart is busy.
          if (control === focused) {
            // A control that is already marked (product-form.js marks its own Add button while it adds) is left to its owner.
            if (control.getAttribute("aria-disabled") === "true") return;
            this.softDisabled.set(control, control.getAttribute("aria-disabled"));
            control.setAttribute("aria-disabled", "true");
            return;
          }
          this.disabledControls.set(control, control.disabled);
          control.disabled = true;
        });
      } else {
        this.disabledControls?.forEach((disabled, control) => { control.disabled = disabled; });
        this.disabledControls?.clear();
        this.softDisabled?.forEach((previous, control) => {
          if (previous === null) control.removeAttribute("aria-disabled");
          else control.setAttribute("aria-disabled", previous);
        });
        this.softDisabled?.clear();
        if (this.refreshQueued && this.isConnected) this.scheduleRefresh(this.openAfterRefresh);
      }
    }

    async changeQuantity(row, quantity) {
      if (!row || this.mutating) return;
      const input = row.querySelector("[data-cq-quantity-input]");
      if (!input) return;
      const previous = Number(row.dataset.quantity);
      if (!Number.isInteger(quantity) || quantity < 0) { input.value = previous; return; }
      if (quantity === previous) return;
      const key = row.dataset.key;
      const variantId = Number(row.dataset.variantId);
      const focused = document.activeElement;
      const focusAction = focused?.dataset.cqQuantity || (focused?.hasAttribute("data-cq-remove") ? "remove" : "input");
      const linePrice = row.querySelector("[data-cq-line-price]");
      const previousLine = linePrice?.textContent;
      // Every subtotal on the surface (the summary, and the page's phone checkout bar) moves with the line.
      const subtotals = Array.from(this.querySelectorAll("[data-cq-subtotal]"));
      const previousSubtotals = subtotals.map((element) => element.textContent);
      input.value = quantity;
      if (linePrice) linePrice.textContent = this.money(Number(row.dataset.unitPrice) * quantity, previousLine);
      const nextTotal = this.totalPrice - Number(row.dataset.linePrice) + Number(row.dataset.unitPrice) * quantity;
      subtotals.forEach((element, index) => { element.textContent = this.money(nextTotal, previousSubtotals[index]); });
      const preview = { undo: [] };
      const emptied = this.previewQuantity(row, quantity, nextTotal, preview);
      row.querySelector("[data-cq-line-error]")?.setAttribute("hidden", "");
      row.setAttribute("aria-busy", "true");
      this.setBusy(true);
      const leaving = quantity === 0 && !emptied ? this.collapseRow(row) : null;
      try {
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({ id: key, quantity, sections: this.sectionNames(), sections_url: location.pathname }),
        });
        const cart = await response.json();
        if (!response.ok || cart.errors || cart.status) throw new Error(cart.description || (typeof cart.errors === "string" ? cart.errors : cart.message) || cartStrings.error);
        if (leaving) await leaving;
        const selector = focusAction === "input" ? "[data-cq-quantity-input]" : focusAction === "remove" ? "[data-cq-remove]" : `[data-cq-quantity="${focusAction}"]`;
        // Focus moves to the same control in the re-rendered line in the same task as the swap, so it never falls to the page.
        this.focusAfterRender = () => this.findRow(key)?.querySelector(selector) || this.focusHome();
        this.renderContents(cart);
        this.focusAfterRender = null;
        const actual = cart.items?.find((item) => item.key === key);
        const newRow = this.findRow(key);
        if (actual && actual.quantity !== quantity) this.lineError(newRow, cartStrings.quantityError.replace("[quantity]", actual.quantity));
        requestAnimationFrame(() => {
          if (this.isOpen && (!document.activeElement || document.activeElement === document.body)) (newRow?.querySelector(selector) || this.focusHome())?.focus({ preventScroll: true });
        });
        if (!actual || actual.quantity === quantity) this.announce(`${this.dataset.title} (${cart.item_count})`);
        document.dispatchEvent(new CustomEvent(quantity === 0 ? "cart:item-remove" : "cart:item-quantity-update", { detail: { cart, context: "MiniCart" } }));
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") publish(PUB_SUB_EVENTS.cartUpdate, { source: "cq-cart-drawer", cartData: cart, variantId });
        this.notifyBold();
      } catch (error) {
        this.undoForAdd(preview);
        this.restoreRow(row);
        input.value = previous;
        if (linePrice) linePrice.textContent = previousLine;
        subtotals.forEach((element, index) => { element.textContent = previousSubtotals[index]; });
        this.lineError(row, error.message || cartStrings.error);
      } finally {
        row.removeAttribute("aria-busy");
        this.setBusy(false);
      }
    }

    // What a quantity change will change on screen changes with the tap (round 9 check), so nothing moves when Shopify answers
    // after the tap's input window (0.5 s; this store answers in 0.35 to 0.9 s): the savings row (its amount, or the row once no
    // saving is left), the cart page's installments row at its $50 line, the wallet row when the last subscription
    // leaves, the cart page tracker's space when the last media or commercial item leaves, and, when the last line leaves, the
    // empty layout (the page) or the empty bottom bar (the side cart). Returns true when the page took its empty layout.
    previewQuantity(row, quantity, nextTotal, preview) {
      const state = this.querySelector("[data-cq-cart-state]");
      const others = Array.from(this.querySelectorAll(".cq-cart-line[data-key]")).filter((other) => other !== row);
      const previous = Number(row.dataset.quantity) || 1;
      const leaving = quantity === 0;
      // The cart's count includes add-on lines the list does not show, so this is the last item only when nothing else is left.
      if (leaving && Number(state?.dataset.count) === previous) {
        if (this.page && this.emptyPage(preview)) return true;
        if (!this.page) {
          this.setHidden(preview, this.querySelector("#header-mini-cart-footer .cq-cart-footer"), true);
          this.setHidden(preview, this.querySelector("[data-cq-cart-express]"), true);
          return false;
        }
      }
      const savings = this.querySelector("[data-cq-savings]");
      const lineSaving = Number(row.dataset.lineSaving) || 0;
      if (savings && lineSaving) {
        const left = (Number(savings.dataset.cqSavings) || 0) - lineSaving + Math.round((lineSaving / previous) * quantity);
        const amount = savings.querySelector("[data-cq-savings-amount]");
        if (left <= 0) this.setHidden(preview, savings, true);
        else if (amount) {
          const before = amount.textContent;
          amount.textContent = `\u2212${this.money(left, before.replace("\u2212", ""))}`;
          preview.undo.push(() => { amount.textContent = before; });
        }
      }
      if (leaving && row.dataset.subscribed === "true" && !others.some((other) => other.dataset.subscribed === "true")) {
        this.previewSubscription(preview, false);
      }
      if (this.page) {
        this.previewTerms(nextTotal, preview);
        if (leaving) this.previewTracker(row, others, state, preview);
      }
      return false;
    }

    setHidden(pending, element, hidden) {
      if (!element || element.hidden === hidden) return;
      element.hidden = hidden;
      pending.undo.push(() => { element.hidden = !hidden; });
    }

    // The page's installments row shows from $50 (cq-cart-drawer-footer, TERMS_MIN): it leaves with the tap when the total
    // drops under $50, and a total that reaches $50 holds the row's 22 px until Shopify's line arrives with the answer.
    previewTerms(total, pending) {
      const terms = this.querySelector(".cq-cart-footer .cq-cart-terms");
      if (terms) {
        this.setHidden(pending, terms, total < TERMS_MIN);
        return;
      }
      const subtotal = this.querySelector(".cq-cart-footer .cq-cart-subtotal");
      if (total < TERMS_MIN || !subtotal) return;
      const hold = document.createElement("div");
      hold.className = "cq-cart-terms cq-cart-terms--hold";
      hold.setAttribute("aria-hidden", "true");
      subtotal.after(hold);
      pending.undo.push(() => hold.remove());
    }

    // The page's tracker shows only while nothing in the cart is excluded (the media and commercial rule of
    // cq-cart-drawer-shipping-eligible). Removing the last excluded item brings it back with Shopify's answer, at the top of the
    // order summary (round 9 check: the summary moved down 68 px about 0.7 s after the tap), so its space is held from the tap
    // by an unseen tracker of the same build and the real one lands in it.
    previewTracker(row, others, state, pending) {
      const ship = this.querySelector("#CQCartShip");
      if (!ship || ship.querySelector("[data-cq-ship]") || !others.length || state?.dataset.shipCurrency !== "true") return;
      const excluded = (state.dataset.shipExcluded || "").split(",").filter(Boolean);
      if (!excluded.length || excluded.some((key) => key !== row.dataset.key)) return;
      const hold = document.createElement("div");
      hold.className = "cq-cart-ship-wrap cq-cart-hold";
      hold.setAttribute("aria-hidden", "true");
      hold.innerHTML = '<div class="cq-cart-ship"><p class="cq-cart-ship-text"><span class="cq-cart-ship-status">\u00a0</span></p><div class="cq-cart-ship-road"></div></div>';
      ship.append(hold);
      pending.undo.push(() => hold.remove());
    }

    // Cart page: the last line leaving switches the page to its empty layout with the tap (round 9 check: the product rows moved
    // from beside the summary to under the empty state when Shopify answered, 0.148 at 1440 x 900). The empty state is the same
    // snippet the page renders for an empty cart (template[data-cq-empty-page], main-cart-items), so Shopify's answer swaps in
    // the same markup in the same place. Its cartridge stays still until then; the answer's copy settles as on load.
    emptyPage(pending) {
      const template = this.querySelector("template[data-cq-empty-page]");
      const lines = this.querySelector("[data-cq-cart-state] > .cq-cart-lines");
      const source = template?.content.querySelector(".cq-cart-empty");
      if (!source || !lines) return false;
      const empty = source.cloneNode(true);
      empty.querySelector(".cq-cart-empty-art")?.classList.add("is-still");
      lines.hidden = true;
      lines.after(empty);
      pending.undo.push(() => {
        empty.remove();
        lines.hidden = false;
      });
      this.setHidden(pending, this.querySelector("[data-cq-cart-bottom]"), true);
      // "Enhance your setup" goes with the last line (Shopify renders none for an empty cart); its row then takes no room.
      const setup = this.querySelector("#CQCartRecs");
      if (setup?.firstChild) {
        const kept = Array.from(setup.childNodes);
        setup.replaceChildren();
        pending.undo.push(() => setup.replaceChildren(...kept));
      }
      const label = this.querySelector("[data-cq-cart-count-label]");
      const zero = template.dataset.countLabel;
      if (label && zero) {
        const before = label.textContent;
        label.textContent = label.hasAttribute("data-cq-brackets") ? `(${zero})` : zero;
        pending.undo.push(() => { label.textContent = before; });
      }
      if (!this.hasAttribute("data-empty")) {
        this.setAttribute("data-empty", "");
        pending.undo.push(() => {
          this.removeAttribute("data-empty");
          this.syncBar();
        });
      }
      this.syncBar();
      // The Remove that had focus is gone; focus goes to the cart heading, as it does after the update.
      this.focusHome()?.focus({ preventScroll: true });
      return true;
    }

    // Height animations stay inside the drawer's own scroller; reduced motion skips them.
    collapseRow(row) {
      if (!row?.animate || this.reducedMotion?.matches) return null;
      const height = row.getBoundingClientRect().height;
      row.style.overflow = "hidden";
      row.cqCollapse = row.animate([
        { height: `${height}px`, opacity: 1 },
        { height: "0px", opacity: 0, paddingTop: "0px", paddingBottom: "0px", borderBottomWidth: "0px" },
      ], { duration: 260, easing: "cubic-bezier(.22, 1, .36, 1)", fill: "forwards" });
      return row.cqCollapse.finished.catch(() => {});
    }

    restoreRow(row) {
      row?.cqCollapse?.cancel();
      if (row) row.style.overflow = "";
    }

    animateLines() {
      const rows = Array.from(this.querySelectorAll(".cq-cart-line[data-key]"));
      const next = new Map(rows.map((row) => [row.dataset.key, Number(row.dataset.quantity)]));
      const previous = this.lineState;
      const previousVariants = this.lineVariants || new Set();
      this.lineState = next;
      this.lineVariants = new Set(rows.map((row) => row.dataset.variantId));
      if (!previous || !this.isOpen || this.reducedMotion?.matches) return;
      rows.forEach((row) => {
        if (row.dataset.cqSeen) return;
        row.dataset.cqSeen = "true";
        const before = previous.get(row.dataset.key);
        // A new key for a product already listed is the same line in a new purchase mode (One time / Subscribe): it stays
        // in place instead of growing in, which would push everything below it.
        if (before === undefined && previousVariants.has(row.dataset.variantId)) return;
        // On the page a new line fades in where it lands; growing it would push the rest of the page down.
        if (before === undefined && row.animate && (this.fadeNewLines || this.page)) {
          row.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 220, easing: "ease-out" });
        } else if (before === undefined && row.animate) {
          const height = row.getBoundingClientRect().height;
          row.style.overflow = "hidden";
          row.animate([
            { height: "0px", opacity: 0, paddingTop: "0px", paddingBottom: "0px" },
            { height: `${height}px`, opacity: 1 },
          ], { duration: 320, easing: "cubic-bezier(.22, 1, .36, 1)" }).finished.catch(() => {}).then(() => { row.style.overflow = ""; });
        } else if (before !== undefined && before < Number(row.dataset.quantity)) {
          row.classList.add("is-updated");
        }
      });
    }

    onPlanMode(input) {
      const row = input.closest("[data-key]");
      const select = row?.querySelector("[data-cq-line-plan-select]");
      if (!row || !select) return;
      if (input.value === "subscribe") {
        select.hidden = false;
        if (row.dataset.subscribed === "true") return;
        // With "Preselect Subscribe and Save" on, the line moves straight to the quick Add plan (Loop's default frequency,
        // snippets/cq-preferred-selling-plan.liquid; Loop's live default in that plan's group when the theme's copy of
        // Loop's defaults is out of date, assets/cq-loop-default.js); the select shows it at once and stays there to
        // change it. With the setting off, the shopper picks a schedule first and nothing changes in the cart until then.
        const options = Array.from(select.options).filter((option) => option.value);
        let preferred = row.querySelector("[data-cq-default-plan]")?.dataset.cqDefaultPlan;
        if (preferred && window.CQLoopDefaults) {
          preferred = window.CQLoopDefaults.follow(preferred, options.map((option) => ({ id: option.value, group: option.dataset.group })));
        }
        if (preferred && options.some((option) => option.value === preferred)) {
          select.value = preferred;
          this.changePlan(row, preferred);
        } else {
          select.focus();
        }
      } else if (row.dataset.subscribed === "true") {
        if (!this.mutating) this.changePlan(row, null, this.previewOneTime(row));
      } else {
        select.hidden = true;
      }
    }

    // Switching a line to One time shows the result at once (no schedule menu, no plan saving, no subscription line when no
    // other line subscribes), so the layout settles with the click instead of when Shopify answers. Returns an undo.
    previewOneTime(row) {
      const changes = [];
      const hide = (element) => {
        if (!element || element.hidden) return;
        element.hidden = true;
        changes.push(() => { element.hidden = false; });
      };
      hide(row.querySelector("[data-cq-line-plan-select]"));
      if (row.dataset.onceSaving !== "true") {
        hide(row.querySelector(".cq-cart-line-savings"));
        const struck = row.querySelector(".cq-cart-price-was");
        if (struck && !struck.hidden) { struck.hidden = true; changes.push(() => { struck.hidden = false; }); }
        const savingsRow = this.querySelector("[data-cq-savings]");
        const lineSaving = Number(row.dataset.lineSaving) || 0;
        if (savingsRow && lineSaving > 0) {
          const amount = savingsRow.querySelector("[data-cq-savings-amount]");
          const left = Number(savingsRow.dataset.cqSavings) - lineSaving;
          if (left <= 0) hide(savingsRow);
          else if (amount) {
            const before = amount.textContent;
            amount.textContent = `\u2212${this.money(left, before)}`;
            changes.push(() => { amount.textContent = before; });
          }
        }
      }
      const priceElement = row.querySelector("[data-cq-line-price]");
      if (priceElement && row.dataset.oncePrice) {
        const before = priceElement.textContent;
        priceElement.textContent = this.money(Number(row.dataset.oncePrice) * Number(row.dataset.quantity), before);
        changes.push(() => { priceElement.textContent = before; });
      }
      // The last subscription turning One time: the line leaves and the wallet row comes back, with the click (r11 item 6).
      const otherSubscribed = Array.from(this.querySelectorAll(".cq-cart-line[data-key]")).some((other) => other !== row && other.dataset.subscribed === "true");
      if (!otherSubscribed) {
        const plans = { undo: [] };
        this.previewSubscription(plans, false);
        changes.push(() => this.undoForAdd(plans));
      }
      return () => changes.forEach((undo) => undo());
    }

    async changePlan(row, planId, undoPreview = null) {
      if (!row || this.mutating) return;
      const line = Number(row.dataset.line);
      const key = row.dataset.key;
      const variantId = row.dataset.variantId;
      const quantity = Number(row.dataset.quantity);
      const focusOnce = planId === null;
      // A line that starts subscribing while no other line does: the wallet row leaves and the subscription line shows with
      // the tap, as for an add on a plan (previewSubscription, r11 item 6; round 9 check: the side cart footer used to change
      // about 0.8 s after the tap, when Shopify answered). A plan that prices the line under its one-time price also adds the
      // line's struck price and "You save" label with the answer; their places are held now too.
      const hold = { undo: [] };
      if (planId && row.dataset.subscribed !== "true") {
        this.previewSubscription(hold, true);
        const option = Array.from(row.querySelectorAll("[data-cq-line-plan-select] option")).find((candidate) => candidate.value === String(planId));
        if (option?.hasAttribute("data-saves")) this.holdLineSavings(row, hold);
      }
      row.querySelector("[data-cq-line-error]")?.setAttribute("hidden", "");
      row.setAttribute("aria-busy", "true");
      this.setBusy(true);
      try {
        // The line is named by its key. A position (line: n) is unreliable here: with a Bold Product Options add-on in the
        // cart, Shopify answered "Cannot apply selling plan to variant" for the right position (round 6, measured).
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({ id: key, quantity, selling_plan: planId || null, sections: this.sectionNames(), sections_url: location.pathname }),
        });
        const cart = await response.json();
        if (!response.ok || cart.errors || cart.status) throw new Error(cart.description || (typeof cart.errors === "string" ? cart.errors : cart.message) || cartStrings.error);
        // The key changes with the plan; focus goes to the same product in its new purchase mode, in the same task as the swap.
        const planTarget = () => {
          const rows = Array.from(this.querySelectorAll(".cq-cart-line[data-key]"));
          const newRow = rows.find((candidate) => candidate.dataset.variantId === variantId && (candidate.dataset.subscribed === "true") === Boolean(planId)) || this.querySelector(`.cq-cart-line[data-line="${line}"]`);
          return (focusOnce ? newRow?.querySelector('[data-cq-plan-mode][value="once"]') : newRow?.querySelector("[data-cq-line-plan-select]")) || this.focusHome();
        };
        this.focusAfterRender = planTarget;
        this.renderContents(cart);
        this.focusAfterRender = null;
        requestAnimationFrame(() => {
          if (this.isOpen && (!document.activeElement || document.activeElement === document.body)) planTarget()?.focus({ preventScroll: true });
        });
        this.announce(`${this.dataset.title} (${cart.item_count})`);
        document.dispatchEvent(new CustomEvent("cart:item-quantity-update", { detail: { cart, context: "MiniCart" } }));
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") publish(PUB_SUB_EVENTS.cartUpdate, { source: "cq-cart-drawer", cartData: cart, variantId: Number(row.dataset.variantId) });
        this.notifyBold();
      } catch (error) {
        undoPreview?.();
        this.undoForAdd(hold);
        const subscribed = row.dataset.subscribed === "true";
        row.querySelectorAll("[data-cq-plan-mode]").forEach((radio) => { radio.checked = (radio.value === "subscribe") === subscribed; });
        const select = row.querySelector("[data-cq-line-plan-select]");
        if (select && !subscribed) { select.value = ""; select.hidden = true; }
        this.lineError(row, error.message || cartStrings.error);
      } finally {
        row.removeAttribute("aria-busy");
        this.setBusy(false);
      }
    }

    findRow(key) {
      return Array.from(this.querySelectorAll("[data-key]")).find((row) => row.dataset.key === key);
    }

    lineError(row, message) {
      const error = row?.querySelector("[data-cq-line-error]") || this.querySelector("#mini-cart-error");
      if (error) { error.hidden = false; error.textContent = message; }
      this.announce(message);
    }

    couponState() {
      const status = this.querySelector("[data-cq-coupon-status]");
      return { open: this.querySelector("[data-cq-coupon]")?.open, value: this.querySelector("#CQCartCoupon")?.value || "", message: status?.textContent || "", kind: status?.dataset.state || "" };
    }

    restoreCoupon(state) {
      const details = this.querySelector("[data-cq-coupon]");
      if (!details) return;
      if (details.open !== Boolean(state.open)) {
        details.setAttribute("data-cq-quiet", "");
        details.open = Boolean(state.open);
      }
      const input = this.querySelector("#CQCartCoupon");
      if (input) {
        input.value = state.value;
        input.setAttribute("aria-invalid", String(state.kind === "error"));
      }
      const status = this.querySelector("[data-cq-coupon-status]");
      if (status) {
        // While a discount hold is on (holdCoupon), the status line keeps the height of its longest message, unseen until the
        // answer's message is in.
        const held = this.couponHold?.status || 0;
        status.textContent = state.message;
        status.hidden = !state.message && !held;
        status.dataset.state = state.kind || "";
        status.classList.toggle("is-held", Boolean(held));
        status.classList.toggle("is-waiting", Boolean(held) && !state.message);
        status.style.minHeight = held ? `${held}px` : "";
        if (status.id) input?.setAttribute("aria-describedby", status.id);
      }
    }

    // An unseen placeholder with a real element's classes, so it takes exactly that element's room until the real one arrives.
    holdElement(tag, className = "") {
      const element = document.createElement(tag);
      if (className) element.className = className;
      element.classList.add("cq-cart-hold");
      element.setAttribute("aria-hidden", "true");
      element.textContent = "\u00a0";
      return element;
    }

    // The line's struck price and "You save" label, held where the line's markup puts them (the label before the plan control).
    holdLineSavings(row, pending) {
      const info = row.querySelector(".cq-cart-line-info");
      const price = row.querySelector(".cq-cart-price");
      const add = (element, parent, before = null) => {
        parent.insertBefore(element, before);
        pending.undo.push(() => element.remove());
      };
      if (price && !price.querySelector(".cq-cart-price-was:not([hidden])")) add(this.holdElement("s", "cq-cart-price-was"), price);
      if (info && !info.querySelector(":scope > .cq-cart-line-savings:not([hidden])")) add(this.holdElement("p", "cq-cart-line-savings"), info, info.querySelector(LINE_AFTER_SAVINGS));
      // The page's summary shows its savings row above the subtotal (the side cart's sits in a row that keeps its height).
      const subtotal = this.page ? this.querySelector(".cq-cart-footer .cq-cart-subtotal") : null;
      if (subtotal && !this.querySelector(".cq-cart-footer .cq-cart-total-savings:not([hidden])")) add(this.holdElement("p", "cq-cart-total-savings"), subtotal.parentElement, subtotal);
    }

    // Discount codes (round 9 check: on /cart a code that applied grew its line by 60 px and the summary by 25 px about 0.7 s
    // after Apply, and the status line pushed the summary down when it appeared). What an Apply can add appears in space held
    // from the tap: the status line keeps the height of its longest message; each line keeps room for what a code adds to it (a
    // struck price, the "You save" label and the code's name), and the summary for its savings row (the page) and an order
    // discount's row under the field. Shopify's answer fills the places the code uses; the rest stay held, unseen, until the
    // shopper's next click or key press, inside whose own input window they let go. The discount field keeps its place on
    // screen both times (keepStill).
    holdCoupon() {
      this.releaseCouponHold();
      const status = this.querySelector("[data-cq-coupon-status]");
      const field = this.querySelector("[data-cq-coupon]");
      if (!status || !field || typeof cartStrings === "undefined") return;
      const keep = this.keepStill("[data-cq-coupon]");
      const lines = new Map();
      this.querySelectorAll(".cq-cart-line[data-key]").forEach((row) => {
        lines.set(row.dataset.key, {
          struck: !row.querySelector(".cq-cart-price .cq-cart-price-was"),
          pill: !row.querySelector(".cq-cart-line-info > .cq-cart-line-savings"),
          codes: (row.querySelector(".cq-cart-line-info > .cq-cart-discounts")?.children.length || 0) + 1,
        });
      });
      status.hidden = false;
      status.classList.add("is-held", "is-waiting");
      status.style.minHeight = "";
      let height = 0;
      [cartStrings.couponError, cartStrings.couponNotApplicable, cartStrings.couponSuccess].filter(Boolean).forEach((message) => {
        status.textContent = message;
        height = Math.max(height, Math.ceil(status.getBoundingClientRect().height));
      });
      this.couponHold = {
        waiting: true,
        status: height,
        lines,
        savings: this.page && !this.querySelector(".cq-cart-footer [data-cq-savings]"),
        codes: (this.couponList()?.children.length || 0) + 1,
      };
      this.restoreCoupon({ ...this.couponState(), message: "", kind: "" });
      this.applyCouponHold();
      keep();
      // Let go on the next click (after its target is set, so nothing moves under the pointer before the click lands) or key press.
      this.couponRelease = new AbortController();
      const release = () => {
        if (this.couponHold && !this.couponHold.waiting) this.releaseCouponHold();
      };
      document.addEventListener("click", release, { capture: true, signal: this.couponRelease.signal });
      document.addEventListener("keydown", release, { capture: true, signal: this.couponRelease.signal });
    }

    // The order discounts' list, right under the discount field.
    couponList() {
      const next = this.querySelector("[data-cq-coupon]")?.nextElementSibling;
      return next?.matches(".cq-cart-discounts") ? next : null;
    }

    // Puts an unseen placeholder in every held place the cart does not fill yet. Run with the tap and after every render while
    // the hold is on (Shopify's answer replaces the lines and the summary).
    applyCouponHold() {
      const hold = this.couponHold;
      if (!hold) return;
      const make = (tag, className = "") => {
        const element = this.holdElement(tag, className);
        element.setAttribute("data-cq-coupon-hold", "");
        return element;
      };
      const holdCodes = (list, count, insert) => {
        const have = list ? list.children.length : 0;
        if (have >= count) return;
        if (!list) {
          list = make("ul", "cq-cart-discounts");
          list.textContent = "";
          insert(list);
        }
        for (let index = have; index < count; index += 1) list.append(make("li"));
      };
      this.querySelectorAll(".cq-cart-line[data-key]").forEach((row) => {
        const want = hold.lines.get(row.dataset.key);
        const info = row.querySelector(".cq-cart-line-info");
        if (!want || !info) return;
        const price = row.querySelector(".cq-cart-price");
        if (want.struck && price && !price.querySelector(".cq-cart-price-was")) price.append(make("s", "cq-cart-price-was"));
        if (want.pill && !info.querySelector(":scope > .cq-cart-line-savings")) {
          info.insertBefore(make("p", "cq-cart-line-savings"), info.querySelector(LINE_AFTER_SAVINGS));
        }
        holdCodes(info.querySelector(":scope > .cq-cart-discounts"), want.codes, (list) => info.insertBefore(list, info.querySelector(":scope > :is(.cq-cart-line-actions, .cq-cart-error)")));
      });
      const field = this.querySelector("[data-cq-coupon]");
      if (field) holdCodes(this.couponList(), hold.codes, (list) => field.after(list));
      const subtotal = this.querySelector(".cq-cart-footer .cq-cart-subtotal");
      if (hold.savings && subtotal && !this.querySelector(".cq-cart-footer .cq-cart-total-savings")) subtotal.before(make("p", "cq-cart-total-savings"));
    }

    // Where the lines sit above the discount field in one column (the cart page on phones and tablets, the side cart), the line
    // places the answer did not fill let go with the answer: the field and everything under it keep their place on screen (the
    // page or the list scrolls by the same amount, keepStill), and the lines above, unchanged, move only with that scroll. When
    // the scroll has no room for it, they stay held until the next click or key press instead. Two columns (the cart page
    // from 990 px) keep them held: the lines' column moves on its own there.
    trimCouponLines() {
      const hold = this.couponHold;
      const field = this.querySelector("[data-cq-coupon]");
      if (!hold || !field || (this.page && !this.barQuery?.matches)) return;
      const held = Array.from(this.querySelectorAll(".cq-cart-line [data-cq-coupon-hold]"));
      if (!held.length) return;
      const keep = this.keepStill("[data-cq-coupon]");
      const room = this.page ? window.scrollY : this.scroller?.scrollTop || 0;
      const top = field.getBoundingClientRect().top;
      held.forEach((element) => element.remove());
      if (top - field.getBoundingClientRect().top > room + 0.5) {
        this.applyCouponHold();
        return;
      }
      hold.lines.clear();
      keep();
    }

    releaseCouponHold() {
      this.couponRelease?.abort();
      this.couponRelease = null;
      if (!this.couponHold) return;
      const keep = this.keepStill("[data-cq-coupon]");
      this.couponHold = null;
      this.querySelectorAll("[data-cq-coupon-hold]").forEach((element) => element.remove());
      const status = this.querySelector("[data-cq-coupon-status]");
      if (status) {
        status.classList.remove("is-held", "is-waiting");
        status.style.minHeight = "";
        if (!status.textContent) status.hidden = true;
      }
      keep();
    }

    // Keeps an element where it is on screen across a layout change made now: the cart page scrolls the window, the side cart
    // its list. The element is named by a selector, so a render in between is followed to its new copy. Returns the function
    // to call after the change.
    keepStill(selector) {
      const scroller = this.page ? null : this.scroller;
      const before = this.querySelector(selector);
      if (!before) return () => {};
      const top = before.getBoundingClientRect().top;
      return () => {
        const element = this.querySelector(selector);
        if (!element) return;
        const delta = element.getBoundingClientRect().top - top;
        if (Math.abs(delta) < 0.5) return;
        if (scroller) scroller.scrollTop += delta;
        else window.scrollBy({ top: delta, behavior: "instant" });
      };
    }

    async applyCoupon() {
      if (this.mutating) return;
      const input = this.querySelector("#CQCartCoupon");
      if (!input) return;
      const code = input.value.trim();
      if (!code) { input.focus(); return; }
      const button = this.querySelector("[data-cq-apply-coupon]");
      this.setBusy(true);
      button?.classList.add("loading");
      button?.querySelector(".button-overlay-spinner")?.classList.remove("hidden");
      this.holdCoupon();
      let message;
      try {
        let codes = [];
        try { codes = JSON.parse(this.querySelector("[data-cq-cart-state]")?.dataset.discountCodes || "[]"); } catch (_) { /* Keep the entered code if no saved codes are available. */ }
        if (!Array.isArray(codes)) codes = [];
        codes = codes.filter((value) => typeof value === "string" && value.toLowerCase() !== code.toLowerCase());
        codes.push(code);
        const response = await fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ discount: codes.join(","), sections: this.sectionNames(), sections_url: location.pathname }),
        });
        const cart = await response.json();
        if (!response.ok || cart.errors || cart.status) throw new Error(cartStrings.couponError);
        const applicable = cart.discount_codes?.some((discount) => typeof discount.code === "string" && discount.code.toLowerCase() === code.toLowerCase() && discount.applicable === true);
        message = applicable ? cartStrings.couponSuccess : cartStrings.couponNotApplicable;
        // Anything the answer adds beyond the held places, above the field (a longer line), keeps the field still on screen.
        const keep = this.keepStill("[data-cq-coupon]");
        this.renderContents(cart);
        this.restoreCoupon({ open: true, value: applicable ? "" : code, message, kind: applicable ? "success" : "error" });
        keep();
        this.trimCouponLines();
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") publish(PUB_SUB_EVENTS.cartUpdate, { source: "cq-cart-drawer", cartData: cart });
        this.notifyBold();
      } catch (error) {
        message = cartStrings.couponError;
        this.restoreCoupon({ open: true, value: code, message, kind: "error" });
      } finally {
        if (this.couponHold) this.couponHold.waiting = false;
        this.setBusy(false);
        button?.classList.remove("loading");
        button?.querySelector(".button-overlay-spinner")?.classList.add("hidden");
        if (this.isOpen) this.querySelector("#CQCartCoupon")?.focus();
        this.announce(message);
      }
    }

    cartSection(cart) {
      const html = cart?.sections?.[this.sectionKey];
      if (typeof html !== "string") return null;
      const section = new DOMParser().parseFromString(html, "text/html");
      return section.querySelector("cq-cart-drawer #header-mini-cart-content [data-cq-cart-state]") && section.querySelector("cq-cart-drawer #header-mini-cart-footer") ? section : null;
    }

    // The empty cart page's first item (round 9 check, AJ 2026-10-09). When Shopify's answer holds items and the page still
    // shows its empty layout, the page's cart (heading, items, summary, product rows) and its reassurance band are replaced at
    // once with the answer's markup: new elements do not count as layout shifts, and nothing that stays moves on screen (what
    // follows the band is off screen before and after). The page then shows the cart from its top (settleFreshPage). Returns
    // the new parts, or null when this is not that case.
    swapFreshPage(doc) {
      if (!this.page || !doc || !this.hasAttribute("data-empty")) return null;
      const state = doc.querySelector("cq-cart-drawer #header-mini-cart-content [data-cq-cart-state]");
      if (!(Number(state?.dataset.count) > 0)) return null;
      const next = doc.querySelector("cq-cart-drawer");
      const pairs = [".cq-cartpage", ".cq-cart-reassure"].map((selector) => [this.querySelector(`:scope > ${selector}`), next.querySelector(`:scope > ${selector}`)]);
      if (pairs.some(([current, incoming]) => !current || !incoming)) return null;
      const parts = pairs.map(([current, incoming]) => {
        const part = document.adoptNode(incoming);
        current.replaceWith(part);
        return part;
      });
      // What watched the old elements watches the new ones: the list and footer swaps, the wallets, the summary's height.
      this.observer?.disconnect();
      ["header-mini-cart-content", "header-mini-cart-footer"].forEach((id) => {
        const target = document.getElementById(id);
        if (target) this.observer?.observe(target, { childList: true });
      });
      this.walletMutations?.disconnect();
      this.walletResize?.disconnect();
      clearInterval(this.expressHold);
      this.walletWidth = undefined;
      this.walletFitWidth = undefined;
      if (this.page || this.drawerObserversReady) this.observeWallets();
      const card = this.querySelector("[data-cq-summary-card]");
      if (card && this.summaryResize) {
        this.summaryResize.disconnect();
        this.summaryResize.observe(card);
      }
      this.pageHold = 0;
      this.holdScroll?.abort();
      this.holdScroll = null;
      // The markup's inline script (which holds the popular row until the related products are in) does not run when inserted.
      const popular = this.querySelector("#CQCartPopular");
      if (popular?.dataset.productId && popular.querySelector("[data-cq-popular-row]")) popular.setAttribute("data-cq-popular-wait", "");
      return parts;
    }

    // After the swap, in the same task: the shopper has just started a cart, so the page shows it from the top, just under the
    // sticky navigation bar (only ever scrolled up; a cart already in view stays put). Scrolling is not a layout shift.
    settleFreshPage(parts) {
      const header = document.getElementById("header");
      const sticky = header && /^(sticky|fixed)$/.test(getComputedStyle(header).position) ? header.getBoundingClientRect().height : 0;
      const top = Math.max(0, Math.round(parts[0].getBoundingClientRect().top + window.scrollY - sticky));
      if (window.scrollY > top) window.scrollTo({ top, behavior: "instant" });
      if (!this.reducedMotion?.matches) parts.forEach((part) => part.animate?.([{ opacity: 0.35 }, { opacity: 1 }], { duration: 240, easing: "ease-out" }));
      this.loadPopular();
    }

    renderContents(cart) {
      if (!cart || !this.isConnected) return false;
      const cartSection = this.cartSection(cart);
      if (!cartSection) {
        this.scheduleRefresh(false);
        return false;
      }
      if (this.renderedCarts.has(cart)) return true;
      this.revision += 1;
      this.renderedCarts.add(cart);
      const coupon = this.couponState();
      const hadFocus = this.contains(document.activeElement);
      // The optimistic row is replaced in place: the real line fades in where it sat instead of growing from zero.
      const bridge = this.querySelector("[data-cq-pending]") ? this.pendingPreview : null;
      this.fadeNewLines = Boolean(bridge);
      // An add lands at the top of the list; on the page, what the shopper is looking at below it stays put.
      const anchor = this.page && this.querySelector("[data-cq-pending]") ? this.pageAnchor(Boolean(this.pending?.fromRecs), this.pending?.anchorFrom) : null;
      // Focus moved off a tapped suggestion stays on the next suggestion's Add when the suggestions are re-rendered.
      const refocusId = this.page ? this.pending?.refocusId : undefined;
      if (refocusId !== undefined && !this.focusAfterRender) {
        this.focusAfterRender = () => Array.from(this.querySelectorAll("#CQCartRecs [data-cross-sell-id]")).find((offer) => offer.dataset.crossSellId === refocusId)?.querySelector(".cq-cart-offer-action") || this.focusHome();
      }
      const sections = cart.sections || {};
      const documents = { [this.sectionKey]: cartSection };
      // The empty cart page's first item: the page is replaced at once (its parts below are then already the new markup).
      const fresh = this.swapFreshPage(cartSection);
      this.getSectionsToRender().forEach((target) => {
        if (typeof sections[target.section] !== "string") return;
        if (fresh?.some((part) => part.contains(document.getElementById(target.id)))) return;
        documents[target.section] ||= new DOMParser().parseFromString(sections[target.section], "text/html");
        const source = documents[target.section].querySelector(target.selector);
        const root = document.getElementById(target.id);
        if (!source || !root) return;
        if (target.id === "CQCartExpress") {
          const currentCheckout = root.querySelector("shopify-accelerated-checkout-cart");
          const nextCheckout = source.querySelector("shopify-accelerated-checkout-cart");
          // Preserve initialized native wallets unless Shopify supplies a different checkout context.
          if (currentCheckout && nextCheckout && ["has-selling-plan", "buyer-country", "buyer-currency"].every((attribute) => currentCheckout.getAttribute(attribute) === nextCheckout.getAttribute(attribute))) return;
          // Shopify draws fresh wallets stacked first (about 210 px), then lays them in one row. Hold the row's height (clipped)
          // until they sit in a row again, so nothing under it jumps down and back.
          this.holdExpress(root);
        }
        const children = root.querySelectorAll(target.selector);
        (children.length ? [...children] : [root]).forEach((element) => { element.innerHTML = source.innerHTML; });
      });
      this.restoreCoupon(coupon);
      this.orphanPending = null;
      this.unrenderedAdd = false;
      const previousKeys = this.lineState;
      this.syncState();
      // A discount hold (holdCoupon) keeps its places in the new markup until the shopper's next click or key press.
      this.applyCouponHold();
      this.fadeNewLines = false;
      if (fresh) this.settleFreshPage(fresh);
      if (anchor) {
        this.keepAnchor(anchor);
        this.holdTail(anchor);
      }
      if (bridge?.src) this.bridgeImages(previousKeys, bridge);
      // From an empty cart the shipping bar and the recommendations arrive with the line; ease them in together.
      if (bridge?.fromEmpty && !this.reducedMotion?.matches) this.querySelector("[data-cq-cart-state]")?.animate?.([{ opacity: 0.35 }, { opacity: 1 }], { duration: 240, easing: "ease-out" });
      // Disabled or replaced controls drop focus to <body>; keep it inside the open dialog. A caller that knows where focus
      // belongs (the same control in the re-rendered line) names it.
      const focusTarget = this.isOpen && hadFocus && !this.modalOpen() ? this.focusAfterRender?.() : null;
      this.focusAfterRender = null;
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      else if (this.isOpen && (hadFocus || !document.activeElement || document.activeElement === document.body) && !this.contains(document.activeElement) && !this.modalOpen()) this.focusFirst();
      return true;
    }

    async updateSectionContents() {
      if (this.mutating) {
        this.refreshQueued = true;
        return false;
      }
      if (this.pendingRefresh) return this.pendingRefresh;
      const revision = this.revision;
      const controller = new AbortController();
      this.refreshController = controller;
      const request = (async () => {
        const url = new URL(location.href);
        url.searchParams.set("sections", this.sectionNames().join(","));
        url.searchParams.set("_cq_cart", String(Date.now()));
        const response = await fetch(url.toString(), { cache: "no-store", signal: controller.signal });
        if (!response.ok) throw new Error(cartStrings.error);
        const sections = await response.json();
        if (!this.cartSection({ sections })) throw new Error(cartStrings.error);
        if (revision !== this.revision || controller.signal.aborted || !this.isConnected) return false;
        return this.renderContents({ sections });
      })();
      this.pendingRefresh = request;
      try { return await request; } catch (error) {
        if (error.name !== "AbortError") throw error;
        return false;
      } finally {
        if (this.pendingRefresh === request) this.pendingRefresh = null;
        if (this.refreshController === controller) this.refreshController = null;
      }
    }

    onCartEvent(event = {}, openAfter = false) {
      // Buy now publishes the normal cart event for integrations, then continues straight to checkout.
      if (event?.checkout === true) return;
      if (event?.source === "cq-cart-drawer") return;
      const candidates = [event?.cartData, event?.cart, event, ...(Array.isArray(event?.items) ? event.items : [])];
      const cart = candidates.find((candidate) => candidate?.sections?.[this.sectionKey]);
      const opener = event?.opener || document.activeElement;
      // The native form publishes both DOM and pubsub events with the same response.
      if (cart && this.renderedCarts.has(cart)) {
        if (this.quietCarts?.has(cart)) return;
        if (openAfter && !this.isOpen) this.open(opener);
        if (openAfter) this.toastFor(cart);
        return;
      }
      if (openAfter && !cart) this.pendingToast = true;
      if (cart && !this.mutating && this.cartSection(cart)) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
        this.refreshController?.abort();
        const shouldOpen = openAfter || this.openAfterRefresh;
        this.openAfterRefresh = false;
        this.refreshQueued = false;
        this.renderContents(cart);
        if (shouldOpen) this.open(opener);
        if (openAfter) this.toastFor(cart);
        return;
      }
      // A later mutation can arrive while a read is in flight; its response must win.
      this.refreshController?.abort();
      this.scheduleRefresh(openAfter, opener);
    }

    scheduleRefresh(openAfter, opener) {
      this.openAfterRefresh ||= openAfter;
      if (openAfter && opener && !this.contains(opener)) this.refreshOpener = opener;
      this.refreshQueued = true;
      clearTimeout(this.refreshTimer);
      if (this.mutating || !this.isConnected) return;
      this.refreshTimer = setTimeout(async () => {
        this.refreshTimer = null;
        if (this.mutating) return;
        const shouldOpen = this.openAfterRefresh;
        const opener = this.refreshOpener;
        this.openAfterRefresh = false;
        this.refreshOpener = null;
        this.refreshQueued = false;
        try {
          // One more try after a short pause: a dropped connection or a busy cart (429) passes in a moment.
          await this.updateSectionContents().catch(() => new Promise((resolve) => setTimeout(resolve, 800)).then(() => this.updateSectionContents()));
          if (shouldOpen) this.open(opener || document.activeElement);
          this.flushToast();
        } catch (error) {
          this.pendingToast = false;
          // An add whose drawer HTML never arrived must not leave its placeholder line spinning.
          const added = Boolean(this.unrenderedAdd || this.orphanPending);
          this.unrenderedAdd = false;
          this.clearOrphanPending();
          if (shouldOpen) this.open(opener || document.activeElement);
          // AJ 2026-10-11: an add Shopify accepted is not reported as a cart error because the drawer's own markup failed to
          // load. The line stays listed, the header count comes from /cart.js, and the drawer loads again when next opened.
          if (added) this.addedWithoutMarkup();
          else this.lineError(null, cartStrings.error);
        }
      }, 40);
    }

    async addedWithoutMarkup() {
      this.staleAfterAdd = true;
      this.announce(this.dataset.added);
      try {
        const response = await fetch(`${window.routes?.cart_url || "/cart"}.js`, { cache: "no-store", headers: { Accept: "application/json" } });
        const count = String((await response.json()).item_count ?? "");
        if (count) document.querySelectorAll("[data-cq-cart-badge]").forEach((badge) => {
          badge.textContent = count;
          badge.toggleAttribute("hidden", count === "0");
        });
      } catch (_) {
        // The badge keeps its count until the next update.
      }
    }

    // The native form sends the same response through a DOM event and pub/sub; confirm each add once.
    toastFor(cart) {
      if (!cart || this.toastedCarts.has(cart)) return;
      this.toastedCarts.add(cart);
      this.pendingToast = true;
      this.flushToast();
    }

    flushToast() {
      if (!this.pendingToast) return;
      this.pendingToast = false;
      if (!this.isOpen || !this.itemCount) return;
      this.showToast();
      this.announce(this.dataset.added);
    }

    announce(message) {
      const live = this.querySelector("[data-cq-cart-live]");
      if (!live) return;
      cancelAnimationFrame(this.announcementFrame);
      live.textContent = "";
      this.announcementFrame = requestAnimationFrame(() => { if (this.isConnected) live.textContent = message || ""; });
    }

    // Recently viewed in the empty cart. The row is held from the frame the empty state shows (placeholders in the same boxes as
    // the cards), so nothing moves when the cards arrive. Round 9: the side cart centres its empty state above the pinned trust
    // row, so it shows the row only when it fits without scrolling (AJ 2026-10-08: "Recently viewed, when there is room"),
    // and it fetches the cards on intent (pointer or focus on the cart icon) so they are usually ready when the cart opens.
    async personalizeEmptyCart() {
      const slot = this.querySelector("[data-cq-empty-personalization]");
      // While a first item is being added the empty state is hidden; do not spend a request on it.
      if (!this.isOpen || this.itemCount || this.pending || !slot) return;
      const block = slot.closest("[data-cq-recent]");
      if (this.recentCards?.length) {
        if (!slot.dataset.personalized) {
          slot.replaceChildren(...this.recentCards.map((card) => card.cloneNode(true)));
          slot.dataset.personalized = "true";
        }
        block?.removeAttribute("hidden");
        block?.removeAttribute("data-cq-reserved");
        this.fitRecent(block);
        this.loadImages();
        return;
      }
      if (this.recentAttempted && !this.recentPromise) {
        this.releaseRecent(block);
        return;
      }
      const ids = this.recentIds();
      if (!ids.length) {
        this.recentAttempted = true;
        this.releaseRecent(block);
        return;
      }
      // The cart page holds the row from the first paint (placeholders; an inline script does it on load). The side cart and an
      // empty state rendered by a cart update get the same hold here, before they paint.
      if (block) {
        if (!this.page && !slot.children.length) slot.replaceChildren(...this.recentPlaceholders());
        if (slot.querySelector(".cq-cart-recent-item--placeholder")) {
          block.hidden = false;
          block.setAttribute("data-cq-reserved", "");
          this.fitRecent(block);
        }
      }
      await this.loadRecent(ids);
      if (!this.isConnected || this.itemCount || !slot.isConnected) return;
      if (this.recentCards?.length && this.isOpen) this.personalizeEmptyCart();
      // Nothing to show after all: the held row goes (it sits last, above only the footer).
      else if (!this.recentPromise) this.releaseRecent(block);
    }

    // Fetches the recently viewed cards once (the search section renders them); a fetch stopped by an add starts over next time.
    loadRecent(ids = this.recentIds()) {
      if (this.recentPromise) return this.recentPromise;
      if (this.recentCards?.length || !ids.length) return Promise.resolve();
      this.recentAttempted = true;
      const controller = new AbortController();
      this.recentController = controller;
      const request = (async () => {
        try {
          const url = new URL(routes.search_url, location.origin);
          url.searchParams.set("section_id", "header-mini-cart-recently-viewed");
          url.searchParams.set("type", "product");
          url.searchParams.set("q", ids.map((id) => `id:${id}`).join(" OR "));
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) return;
          const document = new DOMParser().parseFromString(await response.text(), "text/html");
          const results = Array.from(document.querySelectorAll("#CQCartRecentProducts a[data-product-id]"));
          const cards = ids.map((id) => results.find((card) => card.dataset.productId === id)).filter(Boolean);
          if (cards.length && !controller.signal.aborted) this.recentCards = cards;
        } catch (error) {
          // The server-rendered categories remain usable when storage or search is unavailable.
          if (error?.name === "AbortError") this.recentAttempted = false;
        } finally {
          if (this.recentController === controller) this.recentController = null;
          if (this.recentPromise === request) this.recentPromise = null;
        }
      })();
      this.recentPromise = request;
      return request;
    }

    // Side cart: the cards come early when the shopper reaches for the cart icon with an empty cart.
    prefetchRecent() {
      if (this.page || this.itemCount || this.recentAttempted || this.recentCards?.length) return;
      this.loadRecent();
    }

    // Side cart: Recently viewed only when the empty state still fits without scrolling. Runs in the same frame as the reveal,
    // before paint, so a row that does not fit is never seen.
    fitRecent(block) {
      const scroller = this.scroller;
      if (this.page || !block || block.hidden || !scroller) return;
      if (scroller.scrollHeight > scroller.clientHeight + 1) {
        block.hidden = true;
        block.removeAttribute("data-cq-reserved");
      }
    }

    recentPlaceholders() {
      return Array.from({ length: 3 }, () => {
        const item = document.createElement("span");
        item.className = "cq-cart-recent-item cq-cart-recent-item--placeholder";
        item.setAttribute("aria-hidden", "true");
        const tile = document.createElement("span");
        tile.className = "cq-cart-category-image cq-cart-skel";
        const name = document.createElement("span");
        name.className = "cq-cart-recent-name";
        name.append(Object.assign(document.createElement("span"), { className: "cq-cart-skel cq-cart-skel-text" }));
        const price = document.createElement("span");
        price.className = "cq-cart-recent-price";
        price.textContent = "\u00a0";
        item.append(tile, name, price);
        return item;
      });
    }

    // Recently viewed product ids, newest first (the theme's recently-viewed storage), at most three.
    recentIds() {
      let entries;
      try { entries = JSON.parse(localStorage.getItem("recentlyViewedProducts") || "[]"); } catch (_) { return []; }
      if (!Array.isArray(entries)) return [];
      return [...new Set(entries.filter((entry) => entry && /^[1-9]\d*$/.test(String(entry.id)))
        .map((entry, index) => ({ id: String(entry.id), timestamp: Number(entry.timestamp) || 0, index }))
        .sort((a, b) => b.timestamp - a.timestamp || b.index - a.index)
        .map((entry) => entry.id))].slice(0, 3);
    }

    releaseRecent(block) {
      if (!block?.hasAttribute("data-cq-reserved")) return;
      block.removeAttribute("data-cq-reserved");
      block.hidden = true;
    }

    notifyBold() {
      if (window.BOLD?.common?.eventEmitter?.emit) {
        clearTimeout(this.boldTimer);
        this.boldTimer = setTimeout(() => window.BOLD?.common?.eventEmitter?.emit("BOLD_COMMON_cart_loaded"), 800);
      }
    }
  }
  customElements.define("cq-cart-drawer", CQCartDrawer);
}
